/**
 * Expediente del Préstamo — detalle 360 (URL propia: /prestamos/expediente/:noprest).
 *
 * Todo lo relacionado con un financiamiento en pestañas: resumen (cliente,
 * condiciones, vehículo/garantía, fiador), amortización con estado por cuota,
 * recibos de pago y cancelados, iniciales/abonos, incautos, ventas a crédito,
 * matrícula y modificaciones. Acciones: imprimir estado de cuenta y generar
 * un documento del módulo de plantillas para este préstamo.
 */
import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import CircularProgress from "@mui/material/CircularProgress";
import Autocomplete from "@mui/material/Autocomplete";
import { DataGrid } from "@mui/x-data-grid";
import Menu from "@mui/material/Menu";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import MoreHorizOutlinedIcon from "@mui/icons-material/MoreHorizOutlined";
import Collapse from "@mui/material/Collapse";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import SavingsOutlinedIcon from "@mui/icons-material/SavingsOutlined";
import PercentOutlinedIcon from "@mui/icons-material/PercentOutlined";
import MoreTimeOutlinedIcon from "@mui/icons-material/MoreTimeOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CurrencyExchangeOutlinedIcon from "@mui/icons-material/CurrencyExchangeOutlined";
import StoreOutlinedIcon from "@mui/icons-material/StoreOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import SwapHorizOutlinedIcon from "@mui/icons-material/SwapHorizOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import PublishedWithChangesOutlinedIcon from "@mui/icons-material/PublishedWithChangesOutlined";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import DirectionsCarFilledOutlinedIcon from "@mui/icons-material/DirectionsCarFilledOutlined";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import RestoreOutlinedIcon from "@mui/icons-material/RestoreOutlined";
import MoneyOffOutlinedIcon from "@mui/icons-material/MoneyOffOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import { useGlobalMDAlert, useMaterialUIController } from "context";
import {
  FP,
  PageHeader,
  BarraVolver,
  fpInputSx,
  fpMultilineInputSx,
  fpDataGridSx,
  DocumentoField,
} from "components/financepro";
import { documentoValido, tipoDocumento, formatDocumentoAuto } from "lib/documento";
import {
  useFormularioExtra,
  CamposExtraDetalle,
  CamposExtraForm,
  validarCamposExtra,
  seccionesCodeudor,
} from "components/camposextra";
import CancelarPrestamoDialog from "components/prestamos/CancelarPrestamoDialog";
import FotoCliente from "components/clientes/FotoCliente";
import { tienePermisosModulo, puedeAccionPrograma } from "lib/seguridad/permisosPorModulo";
import CobroPagos from "modules/pagos/cobropagos";
import MantenimientoPagos, { imprimirTicketRecibo } from "modules/pagos/mantenimientopagos";
import { usePortalQr, urlPortal } from "lib/portalqr";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { imprimirHtml } from "modules/documentacion/documentos";
import AbonosCapital from "modules/configuracion/abonoscapital";
import DescuentosInteres from "modules/configuracion/descuentosinteres";
import ModificacionPrestamos from "modules/facturacion/modificacionprestamos";
import RefinanciamientoInterno from "modules/facturacion/refinanciamientointerno";
import CambioSucursalPrestamo from "modules/configuracion/cambiosucursalprestamo";
import ReporteCrediticio from "modules/prestamos/reportecrediticio";
import CobroMatricula from "modules/matriculas/cobromatricula";
import TraspasarMatricula from "modules/matriculas/traspasarmatricula";
import HistorialChasis from "modules/motores/historialchasis";
import RegistrarCargo from "modules/incautos/registrarcargo";
import RegistrarIncauto from "modules/incautos/registrarincauto";
import { NuevoLote as NuevaSolicitudIncauto } from "modules/incautos/solicitudesincauto";
import AssignmentLateOutlinedIcon from "@mui/icons-material/AssignmentLateOutlined";
import MantenimientoIncautos from "modules/incautos/mantenimientoincautos";
import ModificarIncautoCargo from "modules/configuracion/modificarincautocargo";
import { useApi } from "lib/api";
import { imprimirReciboInicial } from "modules/facturacion/iniciales";
import { aIso, etiquetaGarantia } from "modules/garantias/calendario";
import { labelTipo } from "lib/documentosvehiculo";
import { estadoPrestamo } from "lib/prestamos";
import EditarGpsDialog from "modules/ventasgenerales/editargpsdialog";
import { FACTOR_PERIODO } from "lib/calculocuotas";

const fmtMoney = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v || "").replace(/,/g, ""));
  if (Number.isNaN(n)) return "0.00";
  return n.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const toNum = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v || "").replace(/,/g, ""));
  return Number.isNaN(n) ? 0 : n;
};

const PERIODO_SINGULAR = { Quincenas: "quincena", Semanas: "semana", Dias: "día" };

// prestamo.interes es la tasa por período (fracción): 0.0147 en quincenas
// = 1.47% por quincena = 2.94% mensual.
function textoInteres(interes, periodos) {
  const porPeriodo = toNum(interes) * 100;
  const factor = FACTOR_PERIODO[periodos];
  if (!factor) return `${porPeriodo.toFixed(2)}% por cuota`;
  const mensual = `${(porPeriodo * factor).toFixed(2)}% mensual`;
  const singular = PERIODO_SINGULAR[periodos];
  return singular ? `${mensual} · ${porPeriodo.toFixed(2)}% por ${singular}` : mensual;
}

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});
const btnGhost = {
  ...btnBase,
  color: FP.color.textSecondary,
  bgcolor: "transparent",
  "&:hover": { bgcolor: FP.color.surfaceAlt, color: FP.color.textPrimary },
};

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 2.4,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

/** Estado vacío de una tabla del expediente: ícono grande + mensaje. */
function VacioTabla({ icono: Icono, texto }) {
  return (
    <Stack alignItems="center" justifyContent="center" spacing={1} sx={{ height: "100%", py: 4 }}>
      <Box
        sx={{
          width: 56,
          height: 56,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: `${FP.color.accentBlue}12`,
          color: FP.color.accentBlue,
        }}
      >
        <Icono sx={{ fontSize: 26 }} />
      </Box>
      <Typography sx={{ fontWeight: 800, color: FP.color.textPrimary }}>No hay registros</Typography>
      <Typography sx={{ fontSize: "0.82rem", color: FP.color.textSecondary, textAlign: "center" }}>
        {texto}
      </Typography>
    </Stack>
  );
}

/**
 * Card colapsable de una tabla del expediente: cabecera con ícono, título,
 * tooltip de información y chevron; la tabla mantiene sus encabezados y
 * muestra un estado vacío ilustrado cuando no hay filas.
 */
function SeccionTabla({ icono: Icono, titulo, descripcion, vacio, rows, columns, extraToolbar }) {
  const [abierta, setAbierta] = useState(true);
  return (
    <Box
      sx={{
        border: `1px solid ${FP.color.border}`,
        borderRadius: 2.5,
        mb: 2.4,
        bgcolor: "#FFFFFF",
        boxShadow: FP.shadow.soft,
        overflow: "hidden",
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.2} sx={{ px: 2.2, py: 1.5 }}>
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: 1.4,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: `${FP.color.navy}10`,
            color: FP.color.navy,
            flexShrink: 0,
          }}
        >
          <Icono sx={{ fontSize: 19 }} />
        </Box>
        <Typography sx={{ fontWeight: 800, fontSize: "0.98rem", color: FP.color.textPrimary }}>
          {titulo}
        </Typography>
        <Box sx={{ flex: 1 }} />
        {extraToolbar}
        {descripcion && (
          <Tooltip title={descripcion}>
            <InfoOutlinedIcon sx={{ fontSize: 18, color: FP.color.textMuted }} />
          </Tooltip>
        )}
        <IconButton size="small" onClick={() => setAbierta((a) => !a)}>
          <ExpandMoreIcon
            sx={{
              fontSize: 20,
              color: FP.color.textSecondary,
              transform: abierta ? "rotate(180deg)" : "none",
              transition: "transform .15s ease",
            }}
          />
        </IconButton>
      </Stack>
      <Collapse in={abierta}>
        <Box sx={{ px: 2.2, pb: 2.2 }}>
          <DataGrid
            autoHeight
            rows={rows}
            columns={columns}
            rowHeight={44}
            columnHeaderHeight={44}
            hideFooter={rows.length <= 25}
            pageSizeOptions={[25, 50, 100]}
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
            slots={{ noRowsOverlay: () => <VacioTabla icono={Icono} texto={vacio} /> }}
            sx={{
              ...fpDataGridSx,
              "--DataGrid-overlayHeight": "210px",
              // Estilo del mockup: tabla con borde propio redondeado y
              // encabezado gris claro con texto oscuro (sin mayúsculas).
              border: `1px solid ${FP.color.border}`,
              borderRadius: 2,
              overflow: "hidden",
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#F7F9FC",
                color: FP.color.textPrimary,
                fontSize: "0.84rem",
                fontWeight: 700,
                letterSpacing: 0,
                textTransform: "none",
                borderBottom: `1px solid ${FP.color.border}`,
              },
              "& .MuiDataGrid-columnHeaderTitle": { fontWeight: 700 },
              "& .MuiDataGrid-columnSeparator": { color: FP.color.border },
              "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus": {
                outline: "none",
              },
              "& .MuiDataGrid-row:last-of-type .MuiDataGrid-cell": {
                borderBottom: "none",
              },
            }}
            disableRowSelectionOnClick
          />
        </Box>
      </Collapse>
    </Box>
  );
}

function Dato({ label, valor, color, onClick }) {
  const clickable = Boolean(onClick && valor);
  return (
    <Grid item xs={6} sm={4} md={3}>
      <Typography
        sx={{
          fontSize: "0.7rem",
          fontWeight: 700,
          color: FP.color.textMuted,
          textTransform: "uppercase",
        }}
      >
        {label}
      </Typography>
      <Typography
        onClick={clickable ? onClick : undefined}
        sx={{
          fontSize: "0.92rem",
          fontWeight: 700,
          color: color || (clickable ? FP.color.accentBlue : FP.color.textPrimary),
          wordBreak: "break-word",
          ...(clickable
            ? {
                cursor: "pointer",
                textDecoration: "underline",
                "&:hover": { color: FP.color.navy },
              }
            : {}),
        }}
      >
        {valor || "—"}
      </Typography>
    </Grid>
  );
}

// ---- Detalle de la venta (factura completa + campos personalizados) ----
// El backend manda factmot con SELECT * porque el esquema varía por tenant:
// las columnas conocidas llevan etiqueta bonita y el resto sale con su
// nombre tal cual, para que "todos los datos" sea literal.
const ETIQUETAS_VENTA = {
  secuencia: "Factura #",
  fecha: "Fecha",
  nombres: "Nombres",
  apellidos: "Apellidos",
  cedula: "Documento",
  marca: "Marca",
  modelo: "Modelo",
  colore: "Color",
  chasis: "Chasis",
  precio: "Precio",
  inicial: "Inicial",
  financia: "Financiado",
  interes: "Interés",
  cuotas: "Cuotas",
  vcuotas: "Valor cuota",
  deudadat: "Deuda pactada",
  sucursal: "Sucursal",
  noprest: "Préstamo",
  ncf: "NCF",
  banco: "Banco",
  referencia: "Referencia",
  vendedor: "Vendedor",
  empresa: "Empresa",
  moneda: "Moneda",
  user: "Registrada por",
  fecha_crea: "Registrada el",
};
const VENTA_OCULTAR = new Set(["fecha_mod", "timestamp"]);
const VENTA_MONTOS = new Set(["precio", "inicial", "financia", "vcuotas", "deudadat"]);

// Datos del cliente que viven en solicit (llegan por el resumen) y que el
// diálogo de la venta muestra y edita junto a nombres y apellidos. Los del
// fiador (garante) se leen solo si el préstamo tiene fiador; al editar van
// en su propia sección, con el documento como cédula, RNC o pasaporte.
const CONTACTO_VENTA = [
  { k: "direccion", label: "Dirección" },
  { k: "urb", label: "Provincia" },
  { k: "telefono", label: "Teléfono" },
  { k: "celular", label: "Celular" },
  { k: "email", label: "Email" },
  { k: "apodo", label: "Apodo" },
  { k: "fiador", label: "Fiador", fiador: true },
  { k: "fiadorced", label: "Documento del fiador", fiador: true },
  { k: "fiadortel", label: "Teléfono del fiador", fiador: true },
  { k: "fiadordir", label: "Dirección del fiador", fiador: true },
];

// Edición rápida de los datos generales del cliente (nombres, apellidos,
// dirección, provincia, teléfonos, email, apodo, comentario y los datos
// del fiador, incluido su documento) y de los campos personalizados de la factura, sin
// abrir Modificar condiciones. Permiso propio: expediente + modificar (no
// ares2040, que además permite recalcular las condiciones del préstamo);
// el backend sincroniza factmot + solicit + prestamo y el registro de
// datosextra. Las secciones de persona del fiador (marcadas "También al
// fiador / co-deudor") se ven y editan aparte, con sus propios valores.
function DetalleVentaDialog({
  open,
  onClose,
  venta,
  secciones,
  valores,
  valoresCodeudor,
  resumen,
  puedeEditar,
  onSaved,
}) {
  const { post } = useApi();
  const { showAlert } = useGlobalMDAlert();
  const [editando, setEditando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [provincias, setProvincias] = useState([]);
  const [form, setForm] = useState({ nombres: "", apellidos: "", direccion: "", provincia: "" });
  const [extra, setExtra] = useState({});
  const [extraCod, setExtraCod] = useState({});
  const [tipodocFiador, setTipodocFiador] = useState("cedula");
  if (!venta) return null;
  // Sin fiador en el préstamo no hay a quién asignarle los campos.
  const tieneFiador = Boolean(String(resumen?.fiadorced || resumen?.fiador || "").trim());
  const seccionesCod = String(resumen?.fiadorced || "").trim() ? seccionesCodeudor(secciones) : [];
  const contactoVisible = CONTACTO_VENTA.filter((c) => !c.fiador || tieneFiador);

  const empezarEdicion = () => {
    // Dirección, provincia y contacto viven en solicit; llegan por el resumen.
    const txt = (k) => String(resumen?.[k] ?? "");
    setForm({
      nombres: String(venta.nombres || ""),
      apellidos: String(venta.apellidos || ""),
      direccion: txt("direccion"),
      provincia: txt("urb"),
      telefono: txt("telefono"),
      celular: txt("celular"),
      email: txt("email"),
      apodo: txt("apodo"),
      comentario: txt("comentario"),
      fiador: txt("fiador"),
      fiadorced: txt("fiadorced") ? formatDocumentoAuto(txt("fiadorced")) : "",
      fiadortel: txt("fiadortel"),
      fiadordir: txt("fiadordir"),
    });
    setTipodocFiador(tipoDocumento(txt("fiadorced")));
    setExtra({ ...(valores || {}) });
    setExtraCod({ ...(valoresCodeudor || {}) });
    setEditando(true);
    if (!provincias.length) {
      post("/api/ares2040_config", {}).then(({ ok, data }) => {
        if (ok && Array.isArray(data.provincias)) setProvincias(data.provincias);
      });
    }
  };

  const guardar = async () => {
    if (!form.nombres.trim() || !form.apellidos.trim() || !form.direccion.trim()) {
      showAlert({ message: "Nombres, apellidos y dirección no pueden estar en blanco", color: "warning" });
      return;
    }
    const errorExtra =
      validarCamposExtra(secciones, extra) || validarCamposExtra(seccionesCod, extraCod, "fiador");
    if (errorExtra) {
      showAlert({ message: errorExtra, color: "warning" });
      return;
    }
    // El documento del fiador solo se valida si cambió (hay datos viejos sucios).
    const fiadorcedAntes = formatDocumentoAuto(String(resumen?.fiadorced || "").trim());
    const fiadorced = form.fiadorced.trim();
    if (fiadorced && !form.fiador.trim()) {
      showAlert({ message: "Indique el nombre del fiador", color: "warning" });
      return;
    }
    if (fiadorced && fiadorced !== fiadorcedAntes && !documentoValido(fiadorced, tipodocFiador)) {
      showAlert({ message: "El documento del fiador está incompleto", color: "warning" });
      return;
    }
    if (form.comentario.length > 1000) {
      showAlert({ message: "El comentario no puede exceder 1000 caracteres", color: "warning" });
      return;
    }
    setGuardando(true);
    try {
      const { ok, data } = await post("/api/expediente_venta_guardar", {
        noprest: String(venta.noprest || resumen?.noprest || ""),
        nombres: form.nombres.trim(),
        apellidos: form.apellidos.trim(),
        direccion: form.direccion.trim(),
        provincia: form.provincia.trim(),
        telefono: form.telefono.trim(),
        celular: form.celular.trim(),
        email: form.email.trim(),
        apodo: form.apodo.trim(),
        comentario: form.comentario.trim(),
        fiador: form.fiador.trim(),
        // Sin tocar, se manda tal como estaba guardado (no reformatear datos viejos).
        fiadorced:
          fiadorced === fiadorcedAntes ? String(resumen?.fiadorced || "").trim() : fiadorced,
        fiadortel: form.fiadortel.trim(),
        fiadordir: form.fiadordir.trim(),
        datosextra: extra,
        datosextracodeudor: seccionesCod.length ? extraCod : {},
      });
      if (ok) {
        showAlert({ message: data.mensajeok || "Datos guardados correctamente", color: "success" });
        setEditando(false);
        onSaved?.();
      } else {
        showAlert({ message: data.Error || "No se pudieron guardar los datos", color: "error" });
      }
    } finally {
      setGuardando(false);
    }
  };

  const cerrar = () => {
    setEditando(false);
    onClose();
  };

  const entradas = Object.entries(venta).filter(
    ([k, v]) =>
      !VENTA_OCULTAR.has(k) &&
      !(editando && (k === "nombres" || k === "apellidos")) &&
      v !== null && v !== undefined && String(v).trim() !== ""
  );
  // Las conocidas primero, en el orden del mapa; las demás al final.
  const conocidas = Object.keys(ETIQUETAS_VENTA);
  entradas.sort(([a], [b]) => {
    const ia = conocidas.indexOf(a);
    const ib = conocidas.indexOf(b);
    return (ia === -1 ? conocidas.length : ia) - (ib === -1 ? conocidas.length : ib);
  });
  return (
    <Dialog open={open} onClose={cerrar} fullWidth maxWidth="md" PaperProps={{ sx: { borderRadius: 2.5 } }}>
      <DialogTitle sx={{ fontWeight: 800 }}>Venta — factura #{venta.secuencia}</DialogTitle>
      <DialogContent>
        {editando && (
          <Box sx={{ ...sectionBox, mb: 2.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
              Datos generales del cliente
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Nombres"
                  value={form.nombres}
                  onChange={(e) => setForm((p) => ({ ...p, nombres: e.target.value }))}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Apellidos"
                  value={form.apellidos}
                  onChange={(e) => setForm((p) => ({ ...p, apellidos: e.target.value }))}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Dirección"
                  value={form.direccion}
                  onChange={(e) => setForm((p) => ({ ...p, direccion: e.target.value }))}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={provincias}
                  value={form.provincia || null}
                  onChange={(_, v) => setForm((p) => ({ ...p, provincia: v || "" }))}
                  renderInput={(params) => (
                    <TextField {...params} label="Provincia" sx={fpInputSx} />
                  )}
                />
              </Grid>
              {contactoVisible
                .filter((c) => !c.fiador && c.k !== "direccion" && c.k !== "urb")
                .map((c) => (
                  <Grid item xs={12} sm={6} key={c.k}>
                    <TextField
                      fullWidth
                      label={c.label}
                      type={c.k === "email" ? "email" : "text"}
                      value={form[c.k]}
                      onChange={(e) => setForm((p) => ({ ...p, [c.k]: e.target.value }))}
                      sx={fpInputSx}
                    />
                  </Grid>
                ))}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  label="Comentario"
                  value={form.comentario}
                  onChange={(e) => setForm((p) => ({ ...p, comentario: e.target.value }))}
                  inputProps={{ maxLength: 1000 }}
                  sx={fpInputSx}
                />
              </Grid>
            </Grid>
          </Box>
        )}
        {editando && (
          <Box sx={{ ...sectionBox, mb: 2.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
              Fiador / garante
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Nombre del fiador"
                  value={form.fiador}
                  onChange={(e) => setForm((p) => ({ ...p, fiador: e.target.value }))}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DocumentoField
                  label="Documento del fiador"
                  value={form.fiadorced}
                  tipo={tipodocFiador}
                  onChangeValor={(v) => setForm((p) => ({ ...p, fiadorced: v }))}
                  onChangeTipo={setTipodocFiador}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Teléfono del fiador"
                  value={form.fiadortel}
                  onChange={(e) => setForm((p) => ({ ...p, fiadortel: e.target.value }))}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Dirección del fiador"
                  value={form.fiadordir}
                  onChange={(e) => setForm((p) => ({ ...p, fiadordir: e.target.value }))}
                  sx={fpInputSx}
                />
              </Grid>
            </Grid>
          </Box>
        )}
        <Grid container spacing={2} sx={{ mb: 2.5 }}>
          {entradas.map(([k, v]) => (
            <Dato
              key={k}
              label={ETIQUETAS_VENTA[k] || k}
              valor={VENTA_MONTOS.has(k) ? `RD$ ${fmtMoney(v)}` : String(v)}
            />
          ))}
          {!editando &&
            contactoVisible
              .filter((c) => String(resumen?.[c.k] ?? "").trim() !== "")
              .map((c) => <Dato key={c.k} label={c.label} valor={String(resumen[c.k])} />)}
        </Grid>
        {!editando && String(resumen?.comentario || "").trim() !== "" && (
          <Box sx={{ mb: 2.5 }}>
            <Typography
              sx={{ fontSize: "0.7rem", fontWeight: 700, color: FP.color.textMuted, textTransform: "uppercase" }}
            >
              Comentario
            </Typography>
            <Typography sx={{ fontSize: "0.92rem", whiteSpace: "pre-wrap" }}>{resumen.comentario}</Typography>
          </Box>
        )}
        {editando ? (
          <CamposExtraForm
            secciones={secciones}
            valores={extra}
            onChange={(id, v) => setExtra((p) => ({ ...p, [id]: v }))}
            sectionSx={{ ...sectionBox, mb: 2.5 }}
          />
        ) : (
          <CamposExtraDetalle secciones={secciones} valores={valores} />
        )}
        {editando ? (
          <CamposExtraForm
            secciones={seccionesCod}
            valores={extraCod}
            onChange={(id, v) => setExtraCod((p) => ({ ...p, [id]: v }))}
            sectionSx={{ ...sectionBox, mb: 2.5 }}
            sufijo="Fiador"
          />
        ) : (
          <CamposExtraDetalle secciones={seccionesCod} valores={valoresCodeudor} sufijo="Fiador" />
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        {editando ? (
          <>
            <Button onClick={() => setEditando(false)} disabled={guardando} sx={btnGhost}>
              Cancelar
            </Button>
            <Button
              startIcon={guardando ? <CircularProgress size={16} color="inherit" /> : <EditOutlinedIcon />}
              onClick={guardar}
              disabled={guardando}
              sx={btnSolid(FP.color.navy, FP.color.navySoft)}
            >
              Guardar
            </Button>
          </>
        ) : (
          <>
            {puedeEditar && (
              <Button startIcon={<EditOutlinedIcon />} onClick={empezarEdicion} sx={btnOutline(FP.color.navy)}>
                Editar
              </Button>
            )}
            <Button onClick={cerrar} sx={btnOutline(FP.color.textSecondary)}>
              Cerrar
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

// Estado de cuenta imprimible con lo ya cargado (sin permisos extra).
function imprimirEstadoCuenta(d) {
  const r = d.resumen;
  // Mismo total que el "Monto en atraso" de la pantalla: cuotas vencidas +
  // mora al día + incautos sin pagar + cargos de gestión.
  const atrasoEstado =
    d.amortizacion
      .filter((a) => a.atrasada)
      .reduce(
        (s, a) =>
          s +
          Math.max(
            0,
            toNum(a.capital) +
              toNum(a.interes) -
              toNum(a.vpagcap) -
              toNum(a.vpagint) -
              toNum(a.descinte)
          ),
        0
      ) +
    toNum(r.morapendiente) +
    toNum(r.incautopendiente) +
    toNum(r.cargospendientes);
  const filasAmort = d.amortizacion
    .map(
      (a) => `<tr>
        <td>${a.nocuota}</td><td>${a.fecha}</td>
        <td style="text-align:right">${fmtMoney(a.capital)}</td>
        <td style="text-align:right">${fmtMoney(a.interes)}</td>
        <td style="text-align:right">${fmtMoney(a.pagadocuota)}</td>
        <td style="text-align:center">${
          a.status === "A" ? (a.atrasada ? "ATRASADA" : "Pendiente") : "Pagada"
        }</td>
      </tr>`
    )
    .join("");
  // Firmas al pie: con el tratamiento de co-deudor encendido se agrega la
  // del co-deudor (el fiador del préstamo) entre el cliente y la empresa.
  const ancho = d.tratamientocodeudor ? 30 : 45;
  const firma = (texto) =>
    `<td style="width:${ancho}%;border-top:1px solid #000;border-bottom:none;padding-top:5px">${texto}</td>`;
  const hueco = `<td style="width:${d.tratamientocodeudor ? 5 : 10}%;border-bottom:none"></td>`;
  const firmas = [
    firma(`${r.nombres || "Cliente"}<br/>${r.cedula || ""}`),
    d.tratamientocodeudor
      ? firma(r.fiador ? `${r.fiador}<br/>${r.fiadorced || ""}<br/>Co-deudor` : "Co-deudor")
      : null,
    firma("Por la empresa"),
  ]
    .filter(Boolean)
    .join(hueco);
  const html = `
    <html><head><meta name="color-scheme" content="light"><title>Estado de cuenta ${r.noprest}</title>
    <style>
      /* margin 0 + border-box: el padding cuenta dentro del alto y el bloque
         de firmas no se pasa a una segunda hoja */
      body{font-family:Arial,sans-serif;font-size:12px;padding:24px;color:#000;
           margin:0;box-sizing:border-box;display:flex;flex-direction:column;min-height:96vh}
      h2,h3{margin:4px 0} table{width:100%;border-collapse:collapse;margin-top:8px}
      td,th{padding:3px 6px;border-bottom:1px solid #ddd} th{background:#eee;text-align:left}
      /* Compromiso y firmas anclados al pie de la hoja */
      .cierre{margin-top:auto;page-break-inside:avoid}
    </style></head><body style="background:#fff;color:#000;background:#fff;color:#000">
      <h2>Estado de Cuenta — Préstamo No. ${r.noprest}</h2>
      <p><b>Cliente:</b> ${r.nombres} · ${r.cedula}<br/>
      <b>Vehículo:</b> ${[r.marca, r.modelo, r.colore].filter(Boolean).join(" ")} ${
    r.chasis ? "· Chasis " + r.chasis : ""
  }<br/>
      <b>Deuda:</b> RD$ ${fmtMoney(r.deuda)} · <b>Pagado:</b> RD$ ${fmtMoney(
    r.pagado
  )} · <b>Balance:</b> RD$ ${fmtMoney(
    toNum(r.balance) + toNum(r.morapendiente)
  )} · <b>Monto en atraso:</b> RD$ ${fmtMoney(atrasoEstado)}${
    toNum(r.completivopendiente) > 0
      ? `<br/><b>Completivo del inicial pendiente:</b> RD$ ${fmtMoney(
          r.completivopendiente
        )} (vencido RD$ ${fmtMoney(r.completivovencido)})`
      : ""
  }</p>
      <h3>Amortización</h3>
      <table><thead><tr><th>Cuota</th><th>Fecha</th><th>Capital</th><th>Interés</th><th>Pagado</th><th>Estado</th></tr></thead>
      <tbody>${filasAmort}</tbody></table>
      <div class="cierre">
        <p style="margin:18px 0 0;font-size:11px">
          Declaro que he recibido y revisado el presente estado de mi financiamiento, estoy conforme
          con los balances aquí indicados y me comprometo a pagar las cuotas pendientes en las
          fechas establecidas.
        </p>
        <p style="margin:12px 0 0;font-size:12px;font-weight:bold">
          Bueno y válido por la suma de RD$ ${fmtMoney(r.deuda)}
        </p>
        <table style="margin-top:55px;font-size:11px;text-align:center">
          <tr>${firmas}</tr>
        </table>
      </div>
      <script>window.onload=function(){window.print();}</script>
    </body></html>`;
  const iframe = document.createElement("iframe");
  iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;";
  document.body.appendChild(iframe);
  const doc = iframe.contentWindow.document;
  doc.open();
  doc.write(html);
  doc.close();
  setTimeout(() => {
    try {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    } catch (e) {
      /* noop */
    }
    setTimeout(() => iframe.remove(), 60000);
  }, 200);
}

function descargarB64(b64, nombre) {
  const bytes = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
  const blob = new Blob([bytes], {
    type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = nombre || "documento.docx";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

const money = (value) => fmtMoney(value);

// Tipos de gestión de cobro (mismo catálogo fijo que valida el backend en
// gestioncobrosbackend.py; si se agrega uno hay que tocar ambos lados).
const TIPOS_GESTION = [
  { valor: "llamada", label: "Llamada" },
  { valor: "visita", label: "Visita" },
  { valor: "whatsapp", label: "WhatsApp" },
  { valor: "promesa", label: "Promesa de pago" },
  { valor: "nolocalizado", label: "No localizado" },
  { valor: "acuerdo", label: "Acuerdo" },
  { valor: "otro", label: "Otro" },
];
const labelTipoGestion = (v) => TIPOS_GESTION.find((t) => t.valor === v)?.label || v;

// Enlace wa.me que abre el chat del cliente con el acceso al portal ya escrito
// (lo envía el usuario desde su WhatsApp). Null si el negocio no tiene el portal
// activo o el cliente no tiene un número utilizable.
function enlaceEnviarPortal(r, portal, negocio) {
  if (!portal) return null;
  let digits = [r?.celular, r?.telefono]
    .map((t) => String(t || "").replace(/\D/g, ""))
    .find((t) => t.length === 10 || t.length === 11);
  if (!digits) return null;
  if (digits.length === 10) digits = `1${digits}`;
  const nombre = String(r?.nombres || "").trim();
  const mensaje =
    `${nombre ? `Hola ${nombre}` : "Hola"}, le saludamos de ${negocio || "nuestra empresa"}.\n\n` +
    `Ya puede ver su cuenta, sus cuotas y sus recibos desde su celular: ` +
    `${urlPortal("expediente", portal.ruta)}\n\n` +
    `Solo tiene que entrar con su número de cédula.`;
  return `https://wa.me/${digits}?text=${encodeURIComponent(mensaje)}`;
}

export default function ExpedienteDetalle() {
  const { noprest } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();
  // QR del portal al pie del recibo reimpreso y botón "Enviar portal" (solo
  // negocios con el portal activo).
  const portal = usePortalQr();

  // Volver a la pantalla anterior (expediente del cliente, informe de
  // préstamos...); la lista general solo como respaldo cuando se entra por
  // enlace directo (location.key "default" = primera entrada del historial).
  const volver = () => {
    if (location.key !== "default") navigate(-1);
    else navigate("/prestamos/expediente");
  };

  const [d, setD] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [tab, setTab] = useState(0);
  const [version, setVersion] = useState(0); // para recargar tras acciones
  // Corregir el número de GPS de una venta general del préstamo (mismo flag
  // que editar la venta: expediente + modificar).
  const [editarGps, setEditarGps] = useState(null);

  // Detalle de la venta: factura completa + campos personalizados (la
  // definición de secciones la sirve el mismo hook de las ventas).
  const [ventaOpen, setVentaOpen] = useState(false);
  const seccionesExtra = useFormularioExtra();

  useEffect(() => {
    let activo = true;
    setCargando(true);
    post("/api/expediente_detalle", { noprest: String(noprest) })
      .then(({ ok, data }) => {
        if (!activo) return;
        if (ok && data.resumen) setD(data);
        else
          showAlert({ message: data.Error || "No se pudo cargar el expediente", color: "error" });
      })
      .finally(() => activo && setCargando(false));
    return () => {
      activo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noprest, version]);

  // ---- Cancelar préstamo (reutiliza el backend de la pantalla de cancelación;
  // requiere permiso `borrar` en cancelarprestamos, el backend lo valida) ----
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelando, setCancelando] = useState(false);
  // ---- Recuperar préstamo cancelado (/api/restaurarprestamo: vuelve a
  // activo, retoma el chasis y cierra el incauto de la cancelación; requiere
  // permiso `modificar` en cancelarprestamos, el backend lo valida) ----
  const [recuperarOpen, setRecuperarOpen] = useState(false);
  const [recuperando, setRecuperando] = useState(false);
  // ---- Incobrable (status 'I'): castiga la deuda sin cancelar; el chasis,
  // la solicitud y la amortización quedan como están para poder quitarle la
  // marca. Mismos flags que cancelar (borrar) y recuperar (modificar). ----
  const [incobrableOpen, setIncobrableOpen] = useState(false);
  const [incobrableMotivo, setIncobrableMotivo] = useState("");
  const [marcandoIncobrable, setMarcandoIncobrable] = useState(false);

  // ---- Acciones sobre el préstamo: cada una abre la pantalla existente en
  // diálogo, precargada con este préstamo cuando la pantalla lo soporta.
  // Al cerrar, el expediente se recarga (version + 1).
  const [controller] = useMaterialUIController();
  const { roleDetalles, nombreNegocio } = controller;
  const [accion, setAccion] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const cerrarAccion = () => {
    setAccion(null);
    setVersion((v) => v + 1);
  };
  // Cancelar un pago específico (por recibo, requiere ares4010 + borrado;
  // el backend re-valida en /api/cancelarpagos).
  // Flag `modificar` del propio expediente: edita los datos generales de
  // la venta y la foto del cliente (el backend re-valida ambos).
  const puedeEditarExpediente = puedeAccionPrograma(roleDetalles, "expediente", "modificar");
  const puedeCancelarPago = puedeAccionPrograma(roleDetalles, "ares4010", "borrar");
  // Reimprimir un recibo tiene permiso propio. Antes colgaba de ares4030 —la
  // pantalla que ANULA pagos—, así que para dejar reimprimir había que
  // conceder también el poder de anular.
  const puedeReimprimir = tienePermisosModulo(roleDetalles, "reimprimirpagos");
  const [pagoCancelar, setPagoCancelar] = useState(null);
  const [cancelandoPago, setCancelandoPago] = useState(false);
  // Candado síncrono: el estado tarda un render y un doble clic mandaba
  // dos anulaciones del mismo recibo (préstamo 37 de García Martínez).
  const cancelandoPagoRef = useRef(false);
  const cancelarPago = async () => {
    if (!pagoCancelar || cancelandoPagoRef.current) return;
    cancelandoPagoRef.current = true;
    setCancelandoPago(true);
    try {
      const { status, data } = await post("/api/cancelarpagos", {
        norecibo: pagoCancelar.norecibo,
        noprest: String(noprest),
        sucursal: pagoCancelar.sucursal || "",
        tipodeusuario: "",
      });
      if (status === 200) {
        showAlert({ message: "Pago cancelado", color: "success" });
        setPagoCancelar(null);
        setVersion((v) => v + 1);
      } else {
        showAlert({ message: data?.Error || "No se pudo cancelar el pago", color: "error" });
      }
    } catch {
      showAlert({ message: "Error de red o servidor", color: "error" });
    } finally {
      cancelandoPagoRef.current = false;
      setCancelandoPago(false);
    }
  };

  // ---- Reimprimir un recibo desde la pestaña de pagos (mismo ticket y
  // endpoint que Mantenimiento Pagos; permiso reimprimirpagos) ----
  const [companiaTicket, setCompaniaTicket] = useState(null);
  const [reimprimiendoRecibo, setReimprimiendoRecibo] = useState(null);
  const reimprimirRecibo = async (norecibo) => {
    setReimprimiendoRecibo(norecibo);
    try {
      let comp = companiaTicket;
      if (!comp) {
        const { ok: okPerfil, data: perfil } = await post("/api/cobropago_perfil", {});
        comp = okPerfil ? perfil.compania || null : null;
        if (comp) setCompaniaTicket(comp);
      }
      const { ok, data } = await post("/api/ares4030_reimprimirrecibo", { norecibo });
      // Multi-empresa: el backend devuelve el membrete de la empresa del pago.
      if (ok && data.recibo) imprimirTicketRecibo(data.recibo, data.compania || comp);
      else showAlert({ message: data.Error || "No se pudo reimprimir el recibo", color: "error" });
    } catch {
      showAlert({ message: "Error de red o servidor", color: "error" });
    } finally {
      setReimprimiendoRecibo(null);
    }
  };

  // ---- Reimprimir el cobro de un inicial (mismo recibo que la pantalla de
  // Iniciales). El completivo cobrado en Cobro de pagos lleva `norecibo` y
  // se reimprime con su recibo de pago (reimprimirRecibo), que trae la
  // línea "Completivo Inicial". Permiso reimprimirpagos en ambos casos. ----
  const [reimprimiendoInicial, setReimprimiendoInicial] = useState(null);
  const reimprimirInicial = async (id) => {
    setReimprimiendoInicial(id);
    try {
      const { ok, data } = await post("/api/expediente_inicial_recibo", { id });
      if (ok && data.recibo) imprimirReciboInicial(data.recibo, data.compania);
      else showAlert({ message: data.Error || "No se pudo reimprimir el inicial", color: "error" });
    } catch {
      showAlert({ message: "Error de red o servidor", color: "error" });
    } finally {
      setReimprimiendoInicial(null);
    }
  };

  // ---- Modificar / eliminar un cargo desde la lista de la pestaña ----
  // Modificar usa /api/modajuste_guardar (ares8070); eliminar usa
  // /api/mantincauto_cancelar (ares5080 + borrado, razón obligatoria).
  const puedeModCargo = tienePermisosModulo(roleDetalles, "ares8070");
  const puedeCancelarCargo = puedeAccionPrograma(roleDetalles, "ares5080", "borrar");
  const cargoVigente = (c) =>
    !["C", "P"].includes(String(c.estado || "").trim().toUpperCase()) && Number(c.valor) > 0;

  const [cargoEditar, setCargoEditar] = useState(null);
  const [cargoValor, setCargoValor] = useState("");
  const [cargoRazon, setCargoRazon] = useState("");
  const [guardandoCargo, setGuardandoCargo] = useState(false);
  const abrirEditarCargo = (c) => {
    setCargoEditar(c);
    setCargoValor(String(c.valor ?? ""));
    setCargoRazon(String(c.razon ?? ""));
  };
  const guardarCargo = async () => {
    if (!cargoEditar) return;
    if (!(parseFloat(cargoValor) > 0)) {
      showAlert({ message: "El valor no puede ser 0", color: "warning" });
      return;
    }
    if (!cargoRazon.trim()) {
      showAlert({ message: "La razón es obligatoria", color: "warning" });
      return;
    }
    setGuardandoCargo(true);
    try {
      const { status, data } = await post("/api/modajuste_guardar", {
        tipo: "cargo",
        id: cargoEditar.id,
        valor: parseFloat(cargoValor),
        razon: cargoRazon.trim(),
      });
      if (status === 200) {
        showAlert({ message: data.ok || "Cargo modificado", color: "success" });
        setCargoEditar(null);
        setVersion((v) => v + 1);
      } else {
        showAlert({ message: data?.Error || "No se pudo modificar el cargo", color: "error" });
      }
    } catch {
      showAlert({ message: "Error de red o servidor", color: "error" });
    } finally {
      setGuardandoCargo(false);
    }
  };

  const [cargoEliminar, setCargoEliminar] = useState(null);
  const [razonEliminar, setRazonEliminar] = useState("");
  const [eliminandoCargo, setEliminandoCargo] = useState(false);
  const eliminarCargo = async () => {
    if (!cargoEliminar) return;
    if (!razonEliminar.trim()) {
      showAlert({ message: "Indique la razón de la cancelación", color: "warning" });
      return;
    }
    setEliminandoCargo(true);
    try {
      const { status, data } = await post("/api/mantincauto_cancelar", {
        tipo: "cargo",
        id: cargoEliminar.id,
        razon: razonEliminar.trim(),
      });
      if (status === 200) {
        showAlert({ message: data.ok || "Cargo cancelado", color: "success" });
        setCargoEliminar(null);
        setRazonEliminar("");
        setVersion((v) => v + 1);
      } else {
        showAlert({ message: data?.Error || "No se pudo cancelar el cargo", color: "error" });
      }
    } catch {
      showAlert({ message: "Error de red o servidor", color: "error" });
    } finally {
      setEliminandoCargo(false);
    }
  };

  // ---- Modificar / eliminar un incauto desde la lista de la pestaña ----
  // Mismos programas que el cargo: modificar = ares8070 (modajuste_guardar),
  // eliminar = ares5080/borrar (mantincauto_cancelar). El editar del incauto
  // solo cambia el valor (la razón no aplica; el backend la ignora).
  const incautoVigente = (i) =>
    !["C", "P"].includes(String(i.estado || "").trim().toUpperCase()) && Number(i.valor) > 0;

  const [incautoEditar, setIncautoEditar] = useState(null);
  const [incautoValor, setIncautoValor] = useState("");
  const [guardandoIncauto, setGuardandoIncauto] = useState(false);
  const abrirEditarIncauto = (i) => {
    setIncautoEditar(i);
    setIncautoValor(String(i.valor ?? ""));
  };
  const guardarIncauto = async () => {
    if (!incautoEditar) return;
    if (!(parseFloat(incautoValor) > 0)) {
      showAlert({ message: "El valor no puede ser 0", color: "warning" });
      return;
    }
    setGuardandoIncauto(true);
    try {
      const { status, data } = await post("/api/modajuste_guardar", {
        tipo: "incauto",
        id: incautoEditar.id,
        valor: parseFloat(incautoValor),
      });
      if (status === 200) {
        showAlert({ message: data.ok || "Incauto modificado", color: "success" });
        setIncautoEditar(null);
        setVersion((v) => v + 1);
      } else {
        showAlert({ message: data?.Error || "No se pudo modificar el incauto", color: "error" });
      }
    } catch {
      showAlert({ message: "Error de red o servidor", color: "error" });
    } finally {
      setGuardandoIncauto(false);
    }
  };

  const [incautoEliminar, setIncautoEliminar] = useState(null);
  const [razonEliminarIncauto, setRazonEliminarIncauto] = useState("");
  const [eliminandoIncauto, setEliminandoIncauto] = useState(false);
  const eliminarIncauto = async () => {
    if (!incautoEliminar) return;
    if (!razonEliminarIncauto.trim()) {
      showAlert({ message: "Indique la razón de la cancelación", color: "warning" });
      return;
    }
    setEliminandoIncauto(true);
    try {
      const { status, data } = await post("/api/mantincauto_cancelar", {
        tipo: "incauto",
        id: incautoEliminar.id,
        razon: razonEliminarIncauto.trim(),
      });
      if (status === 200) {
        showAlert({ message: data.ok || "Incauto cancelado", color: "success" });
        setIncautoEliminar(null);
        setRazonEliminarIncauto("");
        setVersion((v) => v + 1);
      } else {
        showAlert({ message: data?.Error || "No se pudo cancelar el incauto", color: "error" });
      }
    } catch {
      showAlert({ message: "Error de red o servidor", color: "error" });
    } finally {
      setEliminandoIncauto(false);
    }
  };

  // Cambiar chasis (mini-diálogo propio; usa /api/modificachasisincautados)
  const [chasisNuevo, setChasisNuevo] = useState("");
  const [cambiandoChasis, setCambiandoChasis] = useState(false);
  const cambiarChasis = async () => {
    if (!chasisNuevo.trim()) {
      showAlert({ message: "Indique el chasis nuevo", color: "warning" });
      return;
    }
    setCambiandoChasis(true);
    try {
      const { status, data } = await post("/api/modificachasisincautados", {
        // El préstamo identifica la factura: sin esto el backend resolvía por
        // chasis, y un chasis revendido tiene más de una factura.
        noprest: String(noprest),
        chasis: d?.resumen?.chasis || "",
        chasisacambiar: chasisNuevo.trim(),
      });
      if (status === 200) {
        showAlert({ message: data.OK || "Chasis cambiado", color: "success" });
        cerrarAccion();
        setChasisNuevo("");
      } else {
        showAlert({ message: data.Error || "No se pudo cambiar el chasis", color: "error" });
      }
    } catch {
      showAlert({ message: "Error de red o servidor", color: "error" });
    } finally {
      setCambiandoChasis(false);
    }
  };

  // Mora y días de gracia del préstamo (mini-diálogo; /api/cambiarmora,
  // permiso informeprestamos + modificar). Cada préstamo guarda los suyos al
  // crearse: cambiar la configuración general no los toca.
  const [moraNueva, setMoraNueva] = useState("");
  const [graciaNueva, setGraciaNueva] = useState("");
  const [cambiandoMora, setCambiandoMora] = useState(false);
  useEffect(() => {
    if (accion !== "mora" || !d?.resumen) return;
    const res = d.resumen;
    setMoraNueva(String(res.tasamora ?? res.moraconfig ?? ""));
    setGraciaNueva(String(res.diasgracia ?? res.graciaconfig ?? ""));
  }, [accion, d]);
  const cambiarMora = async () => {
    const mora = Number(moraNueva);
    const gracia = Number(graciaNueva);
    if (moraNueva === "" || Number.isNaN(mora) || mora < 0 || mora > 100) {
      showAlert({ message: "Indique una mora entre 0 y 100%", color: "warning" });
      return;
    }
    if (graciaNueva === "" || !Number.isInteger(gracia) || gracia < 0 || gracia > 365) {
      showAlert({ message: "Indique los días de gracia (entero de 0 a 365)", color: "warning" });
      return;
    }
    setCambiandoMora(true);
    try {
      const { status, data } = await post("/api/cambiarmora", {
        noprest: String(noprest),
        mora,
        diagracia: gracia,
      });
      if (status === 200) {
        showAlert({ message: data.ok || "Mora actualizada", color: "success" });
        cerrarAccion();
      } else {
        showAlert({ message: data.Error || "No se pudo cambiar la mora", color: "error" });
      }
    } catch {
      showAlert({ message: "Error de red o servidor", color: "error" });
    } finally {
      setCambiandoMora(false);
    }
  };

  // ---- Gestión de cobros (programa gestioncobros): gestor asignado por
  // préstamo + historial de notas de gestión. La pestaña carga con su propio
  // endpoint para no engordar /api/expediente_detalle.
  const puedeVerGestion = tienePermisosModulo(roleDetalles, "gestioncobros");
  const puedeAsignarGestor = puedeAccionPrograma(roleDetalles, "gestioncobros", "modificar");
  const [gestion, setGestion] = useState(null); // {gestor, asignaciones, notas, usuarios}
  const [gestionVersion, setGestionVersion] = useState(0);
  useEffect(() => {
    if (!puedeVerGestion) return undefined;
    let activo = true;
    post("/api/gestioncobros_datos", { noprest: String(noprest) }).then(({ ok, data }) => {
      if (activo && ok) setGestion(data);
    });
    return () => {
      activo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noprest, gestionVersion]);

  // ---- Garantía postventa (módulo Garantías, varinicial.usagarantias):
  // estado de la garantía de esta venta con enlace al módulo. Usa el
  // endpoint de búsqueda del módulo; con el módulo apagado (o sin permiso)
  // responde error y el dato no se muestra.
  const puedeVerGarantia =
    tienePermisosModulo(roleDetalles, "garantias") || tienePermisosModulo(roleDetalles, "taller");
  const chasisVenta = String(d?.resumen?.chasis || "").trim();
  const [garantia, setGarantia] = useState(null); // { texto, color, existe }
  useEffect(() => {
    setGarantia(null);
    if (!puedeVerGarantia || chasisVenta.length < 4) return undefined;
    let activo = true;
    post("/api/garantias_buscar", { texto: chasisVenta }).then(({ ok, data }) => {
      if (!activo || !ok) return;
      const propias = (data.garantias || []).filter(
        (g) => String(g.chasis).trim().toUpperCase() === chasisVenta.toUpperCase()
      );
      // La de esta venta; si no, la más reciente del chasis.
      const g = propias.find((x) => String(x.noprest) === String(noprest)) || propias[0];
      if (!g) {
        setGarantia({ texto: "Sin garantía", existe: false });
        return;
      }
      const { texto, tono } = etiquetaGarantia(g, data.servicios || [], aIso(new Date()));
      const color = { aviso: FP.color.warning, peligro: FP.color.danger }[tono];
      setGarantia({ texto, color, existe: true });
    });
    return () => {
      activo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noprest, chasisVenta]);

  const [asignarOpen, setAsignarOpen] = useState(false);
  const [gestorSel, setGestorSel] = useState("");
  const [gestorNota, setGestorNota] = useState("");
  const [asignando, setAsignando] = useState(false);
  const abrirAsignar = () => {
    setGestorSel(gestion?.gestor?.gestor || "");
    setGestorNota("");
    setAsignarOpen(true);
  };
  const asignarGestor = async () => {
    setAsignando(true);
    try {
      const { status, data } = await post("/api/gestioncobros_asignar", {
        noprest: String(noprest),
        gestor: gestorSel,
        nota: gestorNota.trim(),
      });
      if (status === 200) {
        showAlert({ message: gestorSel ? "Gestor asignado" : "Gestor retirado", color: "success" });
        setAsignarOpen(false);
        setGestionVersion((v) => v + 1);
      } else {
        showAlert({ message: data?.Error || "No se pudo asignar el gestor", color: "error" });
      }
    } catch {
      showAlert({ message: "Error de red o servidor", color: "error" });
    } finally {
      setAsignando(false);
    }
  };

  // Registrar gestión: las notas son inmutables (no hay editar/borrar).
  const [notaOpen, setNotaOpen] = useState(false);
  const [notaTipo, setNotaTipo] = useState("llamada");
  const [notaTexto, setNotaTexto] = useState("");
  const [notaFechaPromesa, setNotaFechaPromesa] = useState("");
  const [notaMontoPromesa, setNotaMontoPromesa] = useState("");
  const [guardandoNota, setGuardandoNota] = useState(false);
  const abrirNota = () => {
    setNotaTipo("llamada");
    setNotaTexto("");
    setNotaFechaPromesa("");
    setNotaMontoPromesa("");
    setNotaOpen(true);
  };
  const registrarNota = async () => {
    if (notaTipo === "promesa" && (!notaFechaPromesa || !(parseFloat(notaMontoPromesa) > 0))) {
      showAlert({ message: "Una promesa de pago requiere fecha y monto", color: "warning" });
      return;
    }
    if (notaTipo !== "promesa" && !notaTexto.trim()) {
      showAlert({ message: "La nota no puede estar vacía", color: "warning" });
      return;
    }
    setGuardandoNota(true);
    try {
      const { status, data } = await post("/api/gestioncobros_registrarnota", {
        noprest: String(noprest),
        tipo: notaTipo,
        nota: notaTexto.trim(),
        fechapromesa: notaFechaPromesa,
        montopromesa: notaMontoPromesa,
      });
      if (status === 200) {
        showAlert({ message: "Gestión registrada", color: "success" });
        setNotaOpen(false);
        setGestionVersion((v) => v + 1);
      } else {
        showAlert({ message: data?.Error || "No se pudo registrar la gestión", color: "error" });
      }
    } catch {
      showAlert({ message: "Error de red o servidor", color: "error" });
    } finally {
      setGuardandoNota(false);
    }
  };

  // Catálogo de acciones: [clave, etiqueta, programa visible, contenido]
  // Solo se cobra a préstamos activos (el backend también lo rechaza): cobrar
  // uno cancelado lo reactivaba sin pasar por "Recuperar préstamo".
  const puedeCobrar =
    tienePermisosModulo(roleDetalles, "ares4010") &&
    String(d?.resumen?.status || "").trim().toUpperCase() === "A";
  const ACCIONES = [
    {
      grupo: "Cobros",
      items: [
        { clave: "cobrar", etiqueta: "Cobrar pago", icono: PaymentsOutlinedIcon, visible: puedeCobrar },
        { clave: "recibos", etiqueta: "Recibos / cancelar último", icono: ReceiptLongOutlinedIcon, visible: tienePermisosModulo(roleDetalles, "ares4030") },
        { clave: "abono", etiqueta: "Abono a capital", icono: SavingsOutlinedIcon, visible: tienePermisosModulo(roleDetalles, "ares8060") },
        { clave: "descinteres", etiqueta: "Descuento de interés", icono: PercentOutlinedIcon, visible: tienePermisosModulo(roleDetalles, "ares3030") },
      ],
    },
    {
      grupo: "Préstamo",
      items: [
        { clave: "modificar", etiqueta: "Modificar condiciones", icono: EditOutlinedIcon, visible: tienePermisosModulo(roleDetalles, "ares2040") },
        {
          clave: "mora",
          etiqueta: "Mora y días de gracia",
          icono: MoreTimeOutlinedIcon,
          visible: puedeAccionPrograma(roleDetalles, "informeprestamos", "modificar"),
        },
        { clave: "refinanciar", etiqueta: "Refinanciar", icono: CurrencyExchangeOutlinedIcon, visible: tienePermisosModulo(roleDetalles, "ares2050") },
        { clave: "sucursal", etiqueta: "Cambio de sucursal", icono: StoreOutlinedIcon, visible: tienePermisosModulo(roleDetalles, "ares8090") },
        { clave: "crediticio", etiqueta: "Reporte crediticio", icono: AssessmentOutlinedIcon, visible: tienePermisosModulo(roleDetalles, "ares7040") },
      ],
    },
    {
      grupo: "Vehículo",
      items: [
        { clave: "matricula", etiqueta: "Cobrar matrícula", icono: BadgeOutlinedIcon, visible: tienePermisosModulo(roleDetalles, "ares6010") },
        { clave: "traspaso", etiqueta: "Traspasar", icono: SwapHorizOutlinedIcon, visible: tienePermisosModulo(roleDetalles, "ares6030") },
        { clave: "historial", etiqueta: "Historial del chasis", icono: HistoryOutlinedIcon, visible: tienePermisosModulo(roleDetalles, "historialchasis") },
        { clave: "cambiarchasis", etiqueta: "Cambiar chasis", icono: PublishedWithChangesOutlinedIcon, visible: puedeAccionPrograma(roleDetalles, "listadochasisincautados", "modificar") },
      ],
    },
    {
      grupo: "Gestión de cobro",
      items: [
        { clave: "cargo", etiqueta: "Registrar cargo", icono: GavelOutlinedIcon, visible: tienePermisosModulo(roleDetalles, "ares5060") },
        {
          clave: "solicitudincauto",
          etiqueta: "Solicitar incauto",
          icono: AssignmentLateOutlinedIcon,
          visible:
            String(d?.resumen?.status || "").trim().toUpperCase() === "A" &&
            (tienePermisosModulo(roleDetalles, "ares5030") || tienePermisosModulo(roleDetalles, "ares5010")),
        },
        { clave: "incauto", etiqueta: "Registrar incauto", icono: DirectionsCarFilledOutlinedIcon, visible: tienePermisosModulo(roleDetalles, "ares5010") },
        { clave: "incautosvig", etiqueta: "Incautos/cargos vigentes", icono: ListAltOutlinedIcon, visible: tienePermisosModulo(roleDetalles, "ares5080") },
        { clave: "modincauto", etiqueta: "Modificar incauto/cargo", icono: TuneOutlinedIcon, visible: tienePermisosModulo(roleDetalles, "ares8070") },
      ],
    },
  ];
  const hayAcciones = ACCIONES.some((g) => g.items.some((i) => i.visible));

  const cancelarPrestamo = async (devolver) => {
    setCancelando(true);
    try {
      const { ok, data } = await post("/api/prestamosbackend_cancelarprestamo", {
        noprest: String(noprest),
        devolverinventario: devolver ? "S" : "N",
      });
      if (ok) {
        showAlert({ message: data.mensajeok || "Préstamo cancelado", color: "success" });
        setCancelOpen(false);
        setVersion((v) => v + 1);
      } else {
        showAlert({ message: data.Error || "No se pudo cancelar el préstamo", color: "error" });
      }
    } finally {
      setCancelando(false);
    }
  };

  const recuperarPrestamo = async () => {
    setRecuperando(true);
    try {
      const { ok, data } = await post("/api/restaurarprestamo", { id: String(noprest) });
      if (ok) {
        showAlert({ message: data.mensajeok || "Préstamo recuperado", color: "success" });
        setRecuperarOpen(false);
        setVersion((v) => v + 1);
      } else {
        showAlert({ message: data.Error || "No se pudo recuperar el préstamo", color: "error" });
      }
    } finally {
      setRecuperando(false);
    }
  };

  // Marca o quita la marca según el estado actual (el backend re-valida).
  const cambiarIncobrable = async (marcar) => {
    setMarcandoIncobrable(true);
    try {
      const { ok, data } = await post(
        marcar ? "/api/prestamo_marcarincobrable" : "/api/prestamo_quitarincobrable",
        { noprest: String(noprest), motivo: incobrableMotivo.trim() }
      );
      if (ok) {
        showAlert({ message: data.mensajeok || "Préstamo actualizado", color: "success" });
        setIncobrableOpen(false);
        setIncobrableMotivo("");
        setVersion((v) => v + 1);
      } else {
        showAlert({ message: data.Error || "No se pudo actualizar el préstamo", color: "error" });
      }
    } finally {
      setMarcandoIncobrable(false);
    }
  };

  // ---- Generar documento del módulo de plantillas ----
  const [docOpen, setDocOpen] = useState(false);
  const [plantillas, setPlantillas] = useState([]);
  const [plantillaSel, setPlantillaSel] = useState("");
  const [generando, setGenerando] = useState(false);

  const abrirGenerarDoc = async () => {
    setDocOpen(true);
    // Con noprest el backend devuelve solo las plantillas de la empresa del
    // préstamo más las compartidas (multi-empresa); modo apagado: todas.
    const { ok, data } = await post("/api/documentos_plantillas", { noprest: String(noprest) });
    if (ok && Array.isArray(data.plantillas)) setPlantillas(data.plantillas);
    else
      showAlert({ message: data.Error || "No se pudieron cargar las plantillas", color: "error" });
  };

  const generarDoc = async (modo) => {
    if (!plantillaSel) {
      showAlert({ message: "Seleccione una plantilla", color: "warning" });
      return;
    }
    setGenerando(true);
    try {
      const { ok, data } = await post("/api/documentos_generar", {
        id: plantillaSel,
        noprest: String(noprest),
      });
      if (!ok) {
        showAlert({ message: data.Error || "No se pudo generar", color: "error" });
        return;
      }
      if (modo === "word" && data.archivo_b64) descargarB64(data.archivo_b64, data.nombre);
      else imprimirHtml(data.html || "", data.nombre);
      setDocOpen(false);
    } finally {
      setGenerando(false);
    }
  };

  if (cargando) {
    return (
      <Box
        sx={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center" }}
      >
        <CircularProgress />
      </Box>
    );
  }
  if (!d) {
    return (
      <Box sx={{ p: 4 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={volver} sx={btnGhost}>
          Volver
        </Button>
      </Box>
    );
  }

  const r = d.resumen;
  const activo = (r.status || "").trim().toUpperCase() === "A";
  // Distingue saldado (deuda en cero) de cancelado manual: en la BD ambos
  // quedan con status 'C' (ver lib/prestamos).
  const saldado = estadoPrestamo(r.status, r.balance) === "saldado";
  const incobrable = estadoPrestamo(r.status, r.balance) === "incobrable";
  const puedeMarcarIncobrable = puedeAccionPrograma(roleDetalles, "cancelarprestamos", "borrar");
  const puedeQuitarIncobrable = puedeAccionPrograma(roleDetalles, "cancelarprestamos", "modificar");
  const atrasadas = d.amortizacion.filter((a) => a.atrasada).length;
  // Monto en atraso = cuotas vencidas (pendiente neto: capital+interés −
  // pagado − descuento, como el "Monto Atraso" del recibo de ares4010) + mora
  // al día + incautos sin pagar + cargos de gestión (los tres del backend,
  // con los mismos criterios de la pantalla de cobro).
  const atrasoCuotas = d.amortizacion
    .filter((a) => a.atrasada)
    .reduce(
      (s, a) =>
        s +
        Math.max(
          0,
          toNum(a.capital) +
            toNum(a.interes) -
            toNum(a.vpagcap) -
            toNum(a.vpagint) -
            toNum(a.descinte)
        ),
      0
    );
  const montoAtraso =
    atrasoCuotas + toNum(r.morapendiente) + toNum(r.incautopendiente) + toNum(r.cargospendientes);
  const serializado = !!(r.chasis || "").trim();

  // Pestaña de pagos: recibos y abonos a capital juntos, del más reciente al
  // más viejo (fecha dd/mm/aaaa; sort estable: a igual fecha se respeta el
  // orden original).
  const claveFecha = (f) =>
    String(f || "")
      .split("/")
      .reverse()
      .join("");
  const filasPagos = [
    ...d.recibos.map((x) => ({ ...x, esabono: false })),
    ...(d.abonoscapital || []).map((a) => ({
      ...a,
      esabono: true,
      norecibo: a.idabono,
      vpagcap: a.valor,
      vpagint: 0,
      vpagmora: 0,
      total: a.valor,
    })),
  ]
    .sort((a, b) => claveFecha(b.fecha).localeCompare(claveFecha(a.fecha)))
    .map((x, i) => ({ ...x, id: i }));

  const tablas = [
    {
      titulo: `Amortización (${d.amortizacion.length})`,
      icono: ListAltOutlinedIcon,
      descripcion: "Cuotas del préstamo con capital, interés, mora y su estado.",
      vacio: "Aún no hay cuotas para este préstamo.",
      rows: d.amortizacion.map((a) => ({ id: a.nocuota, ...a })),
      columns: [
        { field: "nocuota", headerName: "Cuota", width: 80 },
        { field: "fecha", headerName: "Fecha", width: 110 },
        {
          field: "capital",
          headerName: "Capital",
          width: 110,
          align: "right",
          headerAlign: "right",
          valueFormatter: money,
        },
        {
          field: "interes",
          headerName: "Interés",
          width: 110,
          align: "right",
          headerAlign: "right",
          valueFormatter: money,
        },
        {
          field: "mora",
          headerName: "Mora",
          width: 100,
          align: "right",
          headerAlign: "right",
          valueFormatter: money,
        },
        {
          field: "pagadocuota",
          headerName: "Pagado",
          width: 110,
          align: "right",
          headerAlign: "right",
          valueFormatter: money,
        },
        {
          field: "status",
          headerName: "Estado",
          width: 120,
          renderCell: (p) =>
            p.row.status !== "A" ? (
              <Chip
                size="small"
                label="Pagada"
                sx={{ bgcolor: "#E8F5E9", color: "#2E7D32", fontWeight: 700 }}
              />
            ) : p.row.atrasada ? (
              <Chip
                size="small"
                label="Atrasada"
                sx={{ bgcolor: FP.color.dangerSoft, color: FP.color.danger, fontWeight: 700 }}
              />
            ) : (
              <Chip
                size="small"
                label="Pendiente"
                sx={{
                  bgcolor: FP.color.surfaceAlt,
                  color: FP.color.textSecondary,
                  fontWeight: 700,
                }}
              />
            ),
        },
      ],
    },
    {
      titulo: `Pagos (${filasPagos.length})`,
      icono: PaymentsOutlinedIcon,
      descripcion: "Recibos de pago y abonos a capital aplicados al préstamo.",
      vacio: "Aún no hay pagos registrados para este préstamo.",
      // Botones de la pestaña: cobrar abre el formulario precargado.
      acciones: [
        {
          etiqueta: "Cobrar pago",
          visible: puedeCobrar,
          onClick: () => setAccion("cobrar"),
        },
      ],
      rows: filasPagos,
      columns: [
        {
          field: "norecibo",
          headerName: "Recibo",
          width: 150,
          renderCell: (p) =>
            p.row.esabono ? (
              <Tooltip
                title={[
                  `Abono ${p.row.idabono}`,
                  p.row.interesdescontado
                    ? `Interés descontado RD$ ${fmtMoney(p.row.interesdescontado)}`
                    : "",
                  p.row.banco,
                  p.row.referencia,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              >
                <Chip
                  size="small"
                  label="Abono a capital"
                  sx={{ bgcolor: FP.color.surfaceAlt, fontWeight: 700 }}
                />
              </Tooltip>
            ) : (
              p.value
            ),
        },
        { field: "fecha", headerName: "Fecha", width: 110 },
        {
          field: "vpagcap",
          headerName: "Capital",
          width: 110,
          align: "right",
          headerAlign: "right",
          valueFormatter: money,
        },
        {
          field: "vpagint",
          headerName: "Interés",
          width: 110,
          align: "right",
          headerAlign: "right",
          valueFormatter: money,
        },
        {
          field: "vpagmora",
          headerName: "Mora",
          width: 100,
          align: "right",
          headerAlign: "right",
          valueFormatter: money,
        },
        {
          // Recibos que solo cobraron el completivo del inicial.
          field: "completivo",
          headerName: "Completivo",
          width: 110,
          align: "right",
          headerAlign: "right",
          valueFormatter: (v) => (Number(v) > 0 ? money(v) : ""),
        },
        {
          field: "total",
          headerName: "Total",
          width: 120,
          align: "right",
          headerAlign: "right",
          valueFormatter: money,
        },
        ...(puedeReimprimir
          ? [
              {
                field: "reimprimir",
                headerName: "",
                width: 70,
                sortable: false,
                filterable: false,
                align: "center",
                renderCell: (p) =>
                  p.row.esabono ? null : (
                    <Tooltip title="Reimprimir recibo">
                      <span>
                        <IconButton
                          size="small"
                          disabled={reimprimiendoRecibo !== null}
                          onClick={() => reimprimirRecibo(p.row.norecibo)}
                        >
                          {reimprimiendoRecibo === p.row.norecibo ? (
                            <CircularProgress size={16} />
                          ) : (
                            <PrintOutlinedIcon fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  ),
              },
            ]
          : []),
        ...(puedeCancelarPago
          ? [
              {
                field: "acciones",
                headerName: "",
                width: 70,
                sortable: false,
                filterable: false,
                align: "center",
                renderCell: (p) =>
                  p.row.esabono ? null : (
                    <Tooltip title="Cancelar este pago">
                      <IconButton size="small" color="error" onClick={() => setPagoCancelar(p.row)}>
                        <DeleteOutlineOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ),
              },
            ]
          : []),
      ],
      extra: d.pagoscancelados.length
        ? {
            titulo: `Pagos cancelados (${d.pagoscancelados.length})`,
            icono: DeleteOutlineOutlinedIcon,
            descripcion: "Recibos anulados: sus cuotas volvieron a quedar pendientes.",
            vacio: "No hay pagos cancelados.",
            rows: d.pagoscancelados.map((x, i) => ({ id: i, ...x })),
            columns: [
              { field: "norecibo", headerName: "Recibo", width: 110 },
              { field: "fecha", headerName: "Fecha pago", width: 110 },
              { field: "fechacancelacion", headerName: "Cancelado", width: 110 },
              {
                field: "vpagcap",
                headerName: "Capital",
                width: 110,
                align: "right",
                headerAlign: "right",
                valueFormatter: money,
              },
              {
                field: "vpagint",
                headerName: "Interés",
                width: 110,
                align: "right",
                headerAlign: "right",
                valueFormatter: money,
              },
              { field: "user", headerName: "Usuario", width: 120 },
            ],
          }
        : null,
    },
    {
      titulo: `Iniciales (${d.iniciales.length})`,
      icono: PaidOutlinedIcon,
      descripcion: "Pagos iniciales (enganche) abonados al chasis del préstamo.",
      vacio: "Aún no hay iniciales asociados a este préstamo.",
      rows: d.iniciales.map((x, i) => ({ id: i, ...x })),
      columns: [
        { field: "fecha", headerName: "Fecha", width: 120 },
        {
          field: "tipo",
          headerName: "Cobro",
          width: 150,
          // El completivo se cobra en Cobro de pagos y queda ligado a su recibo.
          valueGetter: (_v, r) =>
            r.norecibo ? `Completivo · rec. ${r.norecibo}` : "Inicial",
        },
        {
          field: "valor",
          headerName: "Valor",
          width: 130,
          align: "right",
          headerAlign: "right",
          valueFormatter: money,
        },
        { field: "banco", headerName: "Tipo de pago", width: 140 },
        { field: "referencia", headerName: "Referencia", flex: 1, minWidth: 140 },
        {
          field: "status",
          headerName: "Estado",
          width: 130,
          renderCell: (p) => {
            // 'S' facturado, 'A' anulado (baja lógica de Iniciales), resto pendiente.
            const estados = {
              S: { label: "Facturado", sx: { bgcolor: "#E8F5E9", color: "#2E7D32" } },
              A: { label: "Anulado", sx: { bgcolor: "#FDECEA", color: "#B71C1C" } },
            };
            const e = estados[(p.value || "").trim().toUpperCase()] || {
              label: "Pendiente",
              sx: { bgcolor: FP.color.surfaceAlt, color: FP.color.textSecondary },
            };
            return <Chip size="small" label={e.label} sx={{ ...e.sx, fontWeight: 700 }} />;
          },
        },
        ...(puedeReimprimir
          ? [
              {
                field: "reimprimir",
                headerName: "",
                width: 70,
                sortable: false,
                filterable: false,
                align: "center",
                renderCell: (p) => {
                  const r = p.row;
                  // Anulados (el recibo ya no vale) y anclas en 0.00 de la
                  // venta no tienen recibo que reimprimir.
                  const anulado = String(r.status || "").trim().toUpperCase() === "A";
                  if (anulado || !(Number(r.valor) > 0)) return null;
                  const ocupado =
                    reimprimiendoInicial === r.id || (r.norecibo && reimprimiendoRecibo === r.norecibo);
                  return (
                    <Tooltip
                      title={
                        r.norecibo
                          ? `Reimprimir el recibo de pago ${r.norecibo} (completivo)`
                          : "Reimprimir recibo del inicial"
                      }
                    >
                      <span>
                        <IconButton
                          size="small"
                          disabled={reimprimiendoInicial !== null || reimprimiendoRecibo !== null}
                          onClick={() =>
                            r.norecibo ? reimprimirRecibo(r.norecibo) : reimprimirInicial(r.id)
                          }
                        >
                          {ocupado ? (
                            <CircularProgress size={16} />
                          ) : (
                            <PrintOutlinedIcon fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  );
                },
              },
            ]
          : []),
      ],
    },
    {
      titulo: `Cargos / Incautos (${d.incautos.length + (d.cargos || []).length})`,
      icono: GavelOutlinedIcon,
      acciones: [
        {
          etiqueta: "Modificar incauto/cargo",
          visible: tienePermisosModulo(roleDetalles, "ares8070"),
          onClick: () => setAccion("modincauto"),
        },
      ],
      grupos: [
        {
          titulo: `Cargos (${(d.cargos || []).length})`,
          icono: GavelOutlinedIcon,
          descripcion: "Cargos de gestión de cobro aplicados al préstamo.",
          vacio: "Aún no hay cargos registrados para este préstamo.",
          rows: (d.cargos || []).map((x, i) => ({ ...x, id: x.id ?? i })),
          columns: [
            { field: "fecha", headerName: "Fecha", width: 120 },
            {
              field: "valor",
              headerName: "Valor",
              width: 120,
              align: "right",
              headerAlign: "right",
              valueFormatter: money,
            },
            { field: "razon", headerName: "Razón", flex: 1, minWidth: 160 },
            {
              field: "estado",
              headerName: "Estado",
              width: 110,
              renderCell: (p) =>
                cargoVigente(p.row) ? (
                  <Chip size="small" label="Vigente" sx={{ bgcolor: "#E3F2FD", color: "#1565C0", fontWeight: 700 }} />
                ) : (
                  <Chip
                    size="small"
                    label={String(p.value || "").trim().toUpperCase() === "P" ? "Pagado" : "Cancelado"}
                    sx={{ bgcolor: FP.color.surfaceAlt, color: FP.color.textSecondary, fontWeight: 700 }}
                  />
                ),
            },
            ...(puedeReimprimir
              ? [
                  {
                    // Recibo del cobro del cargo (resuelto por el backend
                    // contra pagosres); mismo ticket y endpoint que la
                    // pestaña de pagos, así que el mismo permiso.
                    field: "reimprimir",
                    headerName: "",
                    width: 60,
                    sortable: false,
                    filterable: false,
                    align: "center",
                    renderCell: (p) =>
                      String(p.row.estado || "")
                        .trim()
                        .toUpperCase() === "P" && p.row.norecibo ? (
                        <Tooltip title="Imprimir recibo del cobro">
                          <span>
                            <IconButton
                              size="small"
                              disabled={reimprimiendoRecibo !== null}
                              onClick={() => reimprimirRecibo(p.row.norecibo)}
                            >
                              {reimprimiendoRecibo === p.row.norecibo ? (
                                <CircularProgress size={16} />
                              ) : (
                                <PrintOutlinedIcon fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      ) : null,
                  },
                ]
              : []),
            ...(puedeModCargo || puedeCancelarCargo
              ? [
                  {
                    field: "acciones",
                    headerName: "",
                    width: 100,
                    sortable: false,
                    filterable: false,
                    align: "center",
                    renderCell: (p) =>
                      cargoVigente(p.row) ? (
                        <Stack direction="row" spacing={0.5}>
                          {puedeModCargo && (
                            <Tooltip title="Modificar cargo">
                              <IconButton size="small" onClick={() => abrirEditarCargo(p.row)}>
                                <EditOutlinedIcon fontSize="small" sx={{ color: FP.color.navy }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {puedeCancelarCargo && (
                            <Tooltip title="Eliminar cargo">
                              <IconButton size="small" color="error" onClick={() => setCargoEliminar(p.row)}>
                                <DeleteOutlineOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      ) : null,
                  },
                ]
              : []),
          ],
        },
        {
          titulo: `Incautos (${d.incautos.length})`,
          icono: DirectionsCarFilledOutlinedIcon,
          descripcion: "Incautaciones del vehículo asociadas al préstamo.",
          vacio: "Aún no hay incautos registrados para este préstamo.",
          rows: d.incautos.map((x, i) => ({ ...x, id: x.id ?? i })),
          columns: [
            { field: "fecha", headerName: "Fecha", width: 120 },
            { field: "chasis", headerName: "Chasis", width: 170 },
            {
              field: "valor",
              headerName: "Valor",
              width: 120,
              align: "right",
              headerAlign: "right",
              valueFormatter: money,
            },
            { field: "incautador", headerName: "Incautador", flex: 1, minWidth: 140 },
            {
              field: "estado",
              headerName: "Estado",
              width: 110,
              renderCell: (p) =>
                incautoVigente(p.row) ? (
                  <Chip size="small" label="Vigente" sx={{ bgcolor: "#E3F2FD", color: "#1565C0", fontWeight: 700 }} />
                ) : (
                  <Chip
                    size="small"
                    label={String(p.value || "").trim().toUpperCase() === "P" ? "Pagado" : "Cancelado"}
                    sx={{ bgcolor: FP.color.surfaceAlt, color: FP.color.textSecondary, fontWeight: 700 }}
                  />
                ),
            },
            ...(puedeModCargo || puedeCancelarCargo
              ? [
                  {
                    field: "acciones",
                    headerName: "",
                    width: 100,
                    sortable: false,
                    filterable: false,
                    align: "center",
                    renderCell: (p) =>
                      incautoVigente(p.row) ? (
                        <Stack direction="row" spacing={0.5}>
                          {puedeModCargo && (
                            <Tooltip title="Modificar incauto">
                              <IconButton size="small" onClick={() => abrirEditarIncauto(p.row)}>
                                <EditOutlinedIcon fontSize="small" sx={{ color: FP.color.navy }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {puedeCancelarCargo && (
                            <Tooltip title="Eliminar incauto">
                              <IconButton size="small" color="error" onClick={() => setIncautoEliminar(p.row)}>
                                <DeleteOutlineOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      ) : null,
                  },
                ]
              : []),
          ],
        },
      ],
    },
    ...(puedeVerGestion
      ? [
          {
            titulo: `Gestión (${(gestion?.notas || []).length})`,
            icono: SupportAgentOutlinedIcon,
            acciones: [
              { etiqueta: "Registrar gestión", visible: true, onClick: abrirNota },
              { etiqueta: "Asignar gestor", visible: puedeAsignarGestor, onClick: abrirAsignar },
            ],
            grupos: [
              {
                titulo: `Notas de gestión (${(gestion?.notas || []).length})`,
                icono: ForumOutlinedIcon,
                descripcion:
                  "Historial de gestiones de cobro (llamadas, visitas, promesas de pago). Las notas no se editan ni se borran.",
                vacio: "Aún no hay gestiones registradas para este préstamo.",
                rows: (gestion?.notas || []).map((n) => ({ ...n })),
                columns: [
                  { field: "fechahora", headerName: "Fecha", width: 160 },
                  { field: "usuario", headerName: "Usuario", width: 120 },
                  {
                    field: "tipo",
                    headerName: "Tipo",
                    width: 140,
                    renderCell: (p) => (
                      <Chip
                        size="small"
                        label={labelTipoGestion(p.value)}
                        sx={
                          p.value === "promesa"
                            ? { bgcolor: "#FFF7E6", color: "#B45309", fontWeight: 700 }
                            : {
                                bgcolor: FP.color.surfaceAlt,
                                color: FP.color.textSecondary,
                                fontWeight: 700,
                              }
                        }
                      />
                    ),
                  },
                  { field: "nota", headerName: "Nota", flex: 1, minWidth: 200 },
                  {
                    field: "fechapromesa",
                    headerName: "Promesa",
                    width: 250,
                    renderCell: (p) =>
                      p.row.fechapromesa ? (
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ height: "100%" }}>
                          <span>{`${p.row.fechapromesa} · RD$ ${fmtMoney(p.row.montopromesa)}`}</span>
                          {/* estadopromesa lo calcula el backend; el cobro la
                              cierra solo y cancelar el recibo la reabre */}
                          {p.row.estadopromesa === "cumplida" ? (
                            <Chip
                              size="small"
                              label="Cumplida"
                              sx={{ bgcolor: "#E8F5E9", color: "#2E7D32", fontWeight: 700 }}
                            />
                          ) : p.row.estadopromesa === "vencida" ? (
                            <Chip
                              size="small"
                              label="Vencida"
                              sx={{
                                bgcolor: FP.color.dangerSoft,
                                color: FP.color.danger,
                                fontWeight: 700,
                              }}
                            />
                          ) : (
                            <Chip
                              size="small"
                              label="Pendiente"
                              sx={{ bgcolor: "#FFF7E6", color: "#B45309", fontWeight: 700 }}
                            />
                          )}
                        </Stack>
                      ) : null,
                  },
                ],
              },
              {
                titulo: `Asignaciones de gestor (${(gestion?.asignaciones || []).length})`,
                icono: SupportAgentOutlinedIcon,
                descripcion:
                  "Quién lleva la cobranza de este préstamo. Reasignar retira la fila vigente y crea otra: el historial completo queda aquí.",
                vacio: "Este préstamo nunca ha tenido gestor asignado.",
                rows: (gestion?.asignaciones || []).map((a) => ({ ...a })),
                columns: [
                  { field: "gestor", headerName: "Gestor", width: 140 },
                  {
                    field: "estado",
                    headerName: "Estado",
                    width: 110,
                    renderCell: (p) =>
                      p.value === "activa" ? (
                        <Chip
                          size="small"
                          label="Vigente"
                          sx={{ bgcolor: "#E8F5E9", color: "#2E7D32", fontWeight: 700 }}
                        />
                      ) : (
                        <Chip
                          size="small"
                          label="Retirada"
                          sx={{
                            bgcolor: FP.color.surfaceAlt,
                            color: FP.color.textSecondary,
                            fontWeight: 700,
                          }}
                        />
                      ),
                  },
                  { field: "asignadopor", headerName: "Asignado por", width: 130 },
                  { field: "fechaasignacion", headerName: "Desde", width: 160 },
                  { field: "fecharetiro", headerName: "Hasta", width: 160 },
                  { field: "nota", headerName: "Nota", flex: 1, minWidth: 140 },
                ],
              },
            ],
          },
        ]
      : []),
    {
      titulo: "Otros",
      icono: MoreHorizOutlinedIcon,
      grupos: [
        {
          titulo: `Ventas a crédito / GPS (${d.ventascredito.length})`,
          icono: StoreOutlinedIcon,
          descripcion:
            "Ventas generales (GPS, seguros, servicios) asociadas al préstamo. " +
            'En las distribuidas en cuotas, "cobrado" es lo que el cliente ya pagó ' +
            "dentro de sus cuotas.",
          vacio: "Aún no hay ventas a crédito o registros de GPS para este préstamo.",
          rows: d.ventascredito.map((x, i) => ({ id: i, ...x })),
          columns: [
            { field: "fecha", headerName: "Fecha", width: 120 },
            { field: "descripcion", headerName: "Descripción", flex: 1, minWidth: 160 },
            {
              field: "gps",
              headerName: "GPS #",
              width: 150,
              renderCell: (params) => (
                <Stack direction="row" alignItems="center" spacing={0.4} sx={{ height: "100%" }}>
                  <span>{params.row.gps || "—"}</span>
                  {puedeEditarExpediente && (
                    <IconButton
                      size="small"
                      title="Cambiar número de GPS"
                      onClick={() => setEditarGps(params.row)}
                    >
                      <EditOutlinedIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  )}
                </Stack>
              ),
            },
            { field: "formacobro", headerName: "Forma de cobro", width: 170 },
            {
              field: "valor",
              headerName: "Valor",
              width: 120,
              align: "right",
              headerAlign: "right",
              valueFormatter: money,
            },
            {
              field: "cobrado",
              headerName: "Cobrado",
              width: 120,
              align: "right",
              headerAlign: "right",
              valueFormatter: money,
            },
            {
              field: "pendiente",
              headerName: "Pendiente",
              width: 120,
              align: "right",
              headerAlign: "right",
              valueFormatter: money,
            },
          ],
        },
        {
          titulo: `Matrícula (${d.matricula.length})`,
          icono: BadgeOutlinedIcon,
          descripcion: "Cobros de matrícula del vehículo del préstamo.",
          vacio: "Aún no hay registros de matrícula asociados a este préstamo.",
          rows: d.matricula.map((x, i) => ({ id: i, ...x })),
          columns: [
            { field: "fecha", headerName: "Fecha", width: 120 },
            {
              field: "tipodocumento",
              headerName: "Documento",
              width: 130,
              valueFormatter: (value) => (value ? labelTipo(value) : "—"),
            },
            {
              field: "valor",
              headerName: "Valor",
              width: 120,
              align: "right",
              headerAlign: "right",
              valueFormatter: money,
            },
            { field: "formacobro", headerName: "Forma de cobro", width: 165 },
            {
              field: "cobrado",
              headerName: "Cobrado",
              width: 120,
              align: "right",
              headerAlign: "right",
              valueFormatter: money,
            },
            {
              field: "pendiente",
              headerName: "Pendiente",
              width: 120,
              align: "right",
              headerAlign: "right",
              valueFormatter: money,
            },
            { field: "ncf", headerName: "NCF", flex: 1, minWidth: 110 },
          ],
        },
        {
          titulo: `Modificaciones (${d.modificaciones.length})`,
          icono: TuneOutlinedIcon,
          descripcion: "Historial de cambios de condiciones del préstamo (ares2040).",
          vacio: "Aún no hay modificaciones registradas para este préstamo.",
          rows: d.modificaciones.map((x, i) => ({ id: i, ...x })),
          columns: [
            { field: "fecha", headerName: "Fecha", width: 120 },
            {
              field: "valornuevo",
              headerName: "Nuevo valor",
              width: 130,
              align: "right",
              headerAlign: "right",
              valueFormatter: money,
            },
            { field: "interesnuevo", headerName: "Interés", width: 100 },
            { field: "plazonuevo", headerName: "Plazo", width: 90 },
            { field: "formapago", headerName: "Forma de pago", width: 130 },
            { field: "usuario", headerName: "Usuario", flex: 1, minWidth: 110 },
          ],
        },
      ],
    },
  ];

  const tablaActual = tablas[tab];

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title={`Préstamo No. ${r.noprest}`}
        subtitle={`${r.nombres} · ${r.cedula}`}
        cta={
          <Stack direction="row" spacing={1.2}>
            {hayAcciones && (
              <Button
                startIcon={<BoltOutlinedIcon />}
                endIcon={<KeyboardArrowDownIcon />}
                onClick={(e) => setMenuAnchor(e.currentTarget)}
                sx={btnSolid(FP.color.navy, FP.color.navySoft)}
              >
                Acciones
              </Button>
            )}
            <Button startIcon={<ArrowBackIcon />} onClick={volver} sx={btnOutline(FP.color.navy)}>
              Volver
            </Button>
          </Stack>
        }
      />

      <Box sx={{ p: { xs: 2, md: 3 } }}>

        {/* Resumen */}
        <Box sx={sectionBox}>
          <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ mb: 2 }}>
            <FotoCliente
              cedula={r.cedula}
              nombre={r.nombres}
              fotoB64={d.foto_b64}
              noprest={r.noprest}
              puedeEditar={puedeEditarExpediente}
            />
            <Box sx={{ flex: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Typography sx={{ fontWeight: 800, fontSize: "1.05rem" }}>{r.nombres}</Typography>
                {saldado ? (
                  <Chip
                    size="small"
                    label="Saldado"
                    sx={{ bgcolor: "#E8F5E9", color: "#2E7D32", fontWeight: 700 }}
                  />
                ) : activo ? (
                  <Chip
                    size="small"
                    label="Activo"
                    sx={{ bgcolor: "#E3F2FD", color: "#1565C0", fontWeight: 700 }}
                  />
                ) : incobrable ? (
                  <Chip
                    size="small"
                    label="Incobrable"
                    sx={{ bgcolor: FP.color.dangerSoft, color: FP.color.danger, fontWeight: 700 }}
                  />
                ) : (
                  <Chip
                    size="small"
                    label="Cancelado"
                    sx={{
                      bgcolor: FP.color.surfaceAlt,
                      color: FP.color.textSecondary,
                      fontWeight: 700,
                    }}
                  />
                )}
                {atrasadas > 0 && (
                  <Chip
                    size="small"
                    label={`${atrasadas} cuotas atrasadas`}
                    sx={{ bgcolor: FP.color.dangerSoft, color: FP.color.danger, fontWeight: 700 }}
                  />
                )}
              </Stack>
              <Typography sx={{ fontSize: "0.82rem", color: FP.color.textSecondary }}>
                {[r.direccion, r.urb].filter(Boolean).join(", ")} ·{" "}
                {[r.telefono, r.celular].filter(Boolean).join(" / ")}
              </Typography>
            </Box>
          </Stack>
          <Grid container spacing={2}>
            <Dato label="Fecha" valor={r.fecha} />
            <Dato label="Sucursal" valor={r.sucursal} />
            <Dato label="Precio" valor={`RD$ ${fmtMoney(r.precio)}`} />
            <Dato label="Inicial" valor={`RD$ ${fmtMoney(r.inicial)}`} />
            <Dato label="Financiado" valor={`RD$ ${fmtMoney(r.financia)}`} />
            <Dato label="Deuda total" valor={`RD$ ${fmtMoney(r.deuda)}`} />
            <Dato label="Interés" valor={textoInteres(r.interes, r.periodos)} />
            <Dato label="Pagado" valor={`RD$ ${fmtMoney(r.pagado)}`} />
            {/* Balance con la mora pendiente al día incluida */}
            <Dato
              label="Balance"
              valor={`RD$ ${fmtMoney(toNum(r.balance) + toNum(r.morapendiente))}`}
            />
            <Dato
              label="Monto en atraso"
              valor={`RD$ ${fmtMoney(montoAtraso)}`}
              color={montoAtraso > 0 ? FP.color.danger : undefined}
            />
            <Dato label="Cuotas" valor={`${r.plazo} ${r.periodos}`} />
            <Dato label="Valor cuota" valor={`RD$ ${fmtMoney(r.vcuotas)}`} />
            <Dato
              label="Último pago"
              valor={r.fultpago ? `${r.fultpago} (RD$ ${fmtMoney(r.montoult)})` : ""}
            />
            <Dato label="Abono a capital" valor={`RD$ ${fmtMoney(r.abonoacapital)}`} />
            {serializado ? (
              <>
                <Dato
                  label="Vehículo"
                  valor={[r.marca, r.modelo, r.colore].filter(Boolean).join(" · ")}
                />
                <Dato
                  label="Chasis"
                  valor={r.chasis}
                  onClick={() =>
                    navigate(`/inventario/historialchasis/${encodeURIComponent(r.chasis)}`)
                  }
                />
                <Dato label="Placa" valor={r.placa} />
                {garantia && (
                  <Dato
                    label="Garantía de chasis"
                    valor={garantia.texto}
                    color={garantia.color}
                    onClick={
                      garantia.existe
                        ? () => navigate(`/garantias?chasis=${encodeURIComponent(r.chasis)}`)
                        : undefined
                    }
                  />
                )}
              </>
            ) : (
              <Dato label="Garantía" valor={r.garantia} />
            )}
            <Dato
              label="Fiador"
              valor={r.fiador ? `${r.fiador} · ${r.fiadorced} · ${r.fiadortel}` : ""}
            />
            {puedeVerGestion && (
              <Dato
                label="Gestor de cobros"
                valor={gestion?.gestor?.gestor || (puedeAsignarGestor ? "Asignar…" : "")}
                onClick={puedeAsignarGestor ? abrirAsignar : undefined}
              />
            )}
          </Grid>
          <Stack
            direction="row"
            spacing={1.2}
            justifyContent="flex-end"
            flexWrap="wrap"
            useFlexGap
            sx={{ mt: 2.4, pt: 2, borderTop: `1px solid ${FP.color.border}` }}
          >
            {Boolean(String(r.cedula || "").trim()) && (
              <Button
                startIcon={<PersonOutlineIcon />}
                onClick={() =>
                  navigate(`/clientes/expediente/${encodeURIComponent(String(r.cedula).trim())}`)
                }
                sx={btnOutline(FP.color.navy)}
              >
                Expediente del cliente
              </Button>
            )}
            {enlaceEnviarPortal(r, portal, nombreNegocio) && (
              <Button
                component="a"
                href={enlaceEnviarPortal(r, portal, nombreNegocio)}
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<WhatsAppIcon />}
                sx={btnOutline("#128C7E")}
              >
                Enviar portal
              </Button>
            )}
            {d.venta && (
              <Button
                startIcon={<ReceiptLongOutlinedIcon />}
                onClick={() => setVentaOpen(true)}
                sx={btnOutline(FP.color.navy)}
              >
                Detalle de venta
              </Button>
            )}
            <Button
              startIcon={<PrintOutlinedIcon />}
              onClick={() => imprimirEstadoCuenta(d)}
              sx={btnOutline(FP.color.navy)}
            >
              Estado de cuenta
            </Button>
            <Button
              startIcon={<DescriptionOutlinedIcon />}
              onClick={abrirGenerarDoc}
              sx={btnOutline(FP.color.navy)}
            >
              Generar documento
            </Button>
            {activo && (
              <Button
                startIcon={<HighlightOffIcon />}
                onClick={() => setCancelOpen(true)}
                sx={btnOutline(FP.color.danger)}
              >
                Cancelar préstamo
              </Button>
            )}
            {activo && !saldado && puedeMarcarIncobrable && (
              <Button
                startIcon={<MoneyOffOutlinedIcon />}
                onClick={() => setIncobrableOpen(true)}
                sx={btnOutline(FP.color.danger)}
              >
                Marcar incobrable
              </Button>
            )}
            {incobrable && puedeQuitarIncobrable && (
              <Button
                startIcon={<RestoreOutlinedIcon />}
                onClick={() => setIncobrableOpen(true)}
                sx={btnOutline(FP.color.navy)}
              >
                Quitar incobrable
              </Button>
            )}
            {/* Cancelado a mano (no saldado): el mismo sitio del botón
                Cancelar ofrece revertirlo. */}
            {!activo && !saldado && !incobrable && (
              <Button
                startIcon={<RestoreOutlinedIcon />}
                onClick={() => setRecuperarOpen(true)}
                sx={btnOutline(FP.color.navy)}
              >
                Recuperar préstamo
              </Button>
            )}
          </Stack>
        </Box>

        {/* Pestañas */}
        <Box sx={{ ...sectionBox, mb: 0 }}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            variant="scrollable"
            allowScrollButtonsMobile
            scrollButtons={false}
            TabIndicatorProps={{ sx: { display: "none" } }}
            sx={{
              mb: 2.4,
              minHeight: 44,
              "& .MuiTabs-flexContainer": { gap: 1, width: "100%" },
              "& .MuiTab-root": { flex: { md: 1 }, maxWidth: "none" },
            }}
          >
            {tablas.map((t) => {
              const Icono = t.icono;
              return (
                <Tab
                  key={t.titulo}
                  icon={Icono ? <Icono sx={{ fontSize: 18 }} /> : undefined}
                  iconPosition="start"
                  label={t.titulo}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: "0.85rem",
                    minHeight: 40,
                    px: 2,
                    borderRadius: 999,
                    border: `1px solid ${FP.color.border}`,
                    color: FP.color.textSecondary,
                    bgcolor: "#FFFFFF",
                    "& .MuiSvgIcon-root": { color: "inherit" },
                    "&:hover": { borderColor: "#0B1220", color: "#0B1220" },
                    // Activo: fondo negro y letra/ícono blancos. El tema de la
                    // plantilla (assets/theme/components/tabs/tab.js) fuerza
                    // color dark con !important, así que hay que devolverle
                    // el !important.
                    "&.MuiTab-root.Mui-selected": {
                      bgcolor: "#0B1220",
                      borderColor: "#0B1220",
                      color: "#FFFFFF !important",
                    },
                    "&.MuiTab-root.Mui-selected .MuiSvgIcon-root": {
                      color: "#FFFFFF !important",
                    },
                  }}
                />
              );
            })}
          </Tabs>

          {/* Botones propios de la pestaña activa */}
          {(tablaActual.acciones || []).some((a) => a.visible) && (
            <Stack direction="row" spacing={1.2} justifyContent="flex-end" sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
              {(tablaActual.acciones || [])
                .filter((a) => a.visible)
                .map((a) => (
                  <Button key={a.etiqueta} onClick={a.onClick} sx={btnSolid(FP.color.navy, FP.color.navySoft)}>
                    {a.etiqueta}
                  </Button>
                ))}
            </Stack>
          )}
          {(tablaActual.grupos || [tablaActual, ...(tablaActual.extra ? [tablaActual.extra] : [])]).map(
            (g) => (
              <SeccionTabla
                key={g.titulo}
                icono={g.icono || tablaActual.icono}
                titulo={g.titulo}
                descripcion={g.descripcion}
                vacio={g.vacio || "No hay registros para este préstamo."}
                rows={g.rows}
                columns={g.columns}
              />
            )
          )}
        </Box>
      </Box>

      {/* Recuperar préstamo cancelado */}
      <Dialog
        open={recuperarOpen}
        onClose={() => !recuperando && setRecuperarOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Recuperar préstamo #{r.noprest}</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "0.92rem", color: FP.color.textSecondary }}>
            El préstamo de <b>{r.nombres}</b> vuelve a estar activo con su tabla de amortización tal
            como quedó, el chasis se marca vendido otra vez y se cierra el incauto que registró la
            cancelación. Requiere permiso de modificación en cancelación de préstamos.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setRecuperarOpen(false)}
            disabled={recuperando}
            sx={btnOutline(FP.color.textSecondary)}
          >
            Volver
          </Button>
          <Button
            startIcon={recuperando ? <CircularProgress size={16} color="inherit" /> : <RestoreOutlinedIcon />}
            onClick={recuperarPrestamo}
            disabled={recuperando}
            sx={btnSolid(FP.color.navy, FP.color.navySoft)}
          >
            Recuperar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Marcar / quitar incobrable */}
      <Dialog
        open={incobrableOpen}
        onClose={() => !marcandoIncobrable && setIncobrableOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>
          {incobrable ? "Quitar incobrable" : "Marcar incobrable"} — préstamo #{r.noprest}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "0.92rem", color: FP.color.textSecondary, mb: 2 }}>
            {incobrable ? (
              <>
                El préstamo de <b>{r.nombres}</b> vuelve a la cartera activa con su tabla de
                amortización tal como estaba y se le puede volver a cobrar.
              </>
            ) : (
              <>
                El préstamo de <b>{r.nombres}</b> sale de la cartera activa con un balance pendiente
                de <b>RD$ {fmtMoney(r.balance)}</b>: deja de generar mora, de aparecer en morosidad y
                no admite cobros. El chasis y la amortización no se tocan y la marca puede quitarse
                desde este expediente.
              </>
            )}
          </Typography>
          <TextField
            label="Motivo (opcional)"
            value={incobrableMotivo}
            onChange={(e) => setIncobrableMotivo(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            inputProps={{ maxLength: 200 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setIncobrableOpen(false)}
            disabled={marcandoIncobrable}
            sx={btnOutline(FP.color.textSecondary)}
          >
            Volver
          </Button>
          <Button
            startIcon={
              marcandoIncobrable ? (
                <CircularProgress size={16} color="inherit" />
              ) : incobrable ? (
                <RestoreOutlinedIcon />
              ) : (
                <MoneyOffOutlinedIcon />
              )
            }
            onClick={() => cambiarIncobrable(!incobrable)}
            disabled={marcandoIncobrable}
            sx={incobrable ? btnSolid(FP.color.navy, FP.color.navySoft) : btnSolid(FP.color.danger, FP.color.danger)}
          >
            {incobrable ? "Quitar marca" : "Marcar incobrable"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancelar préstamo */}
      <CancelarPrestamoDialog
        abierto={cancelOpen}
        noprest={r.noprest}
        chasis={r.chasis}
        tieneChasis={Boolean(String(r.chasis || "").trim())}
        cargando={cancelando}
        cliente={r.nombres}
        detalle={
          <>
            Balance pendiente: <b>RD$ {fmtMoney(r.balance)}</b>. Requiere permiso de borrado y
            puede revertirse desde este expediente con &quot;Recuperar préstamo&quot;.
          </>
        }
        onCerrar={() => setCancelOpen(false)}
        onConfirmar={cancelarPrestamo}
      />

      {/* Generar documento */}
      <Dialog
        open={docOpen}
        onClose={() => setDocOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Generar documento</DialogTitle>
        <DialogContent>
          <TextField
            select
            fullWidth
            label="Plantilla"
            value={plantillaSel}
            onChange={(e) => setPlantillaSel(e.target.value)}
            sx={{ ...fpInputSx, mt: 1 }}
          >
            {plantillas.map((p) => (
              <MenuItem key={p.id} value={p.id}>
                {p.nombre}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDocOpen(false)} sx={btnGhost}>
            Cerrar
          </Button>
          <Button
            onClick={() => generarDoc("word")}
            disabled={generando}
            sx={btnOutline(FP.color.navy)}
          >
            Word
          </Button>
          <Button
            startIcon={
              generando ? <CircularProgress size={16} color="inherit" /> : <PrintOutlinedIcon />
            }
            onClick={() => generarDoc("imprimir")}
            disabled={generando}
            sx={btnSolid(FP.color.navy, FP.color.navySoft)}
          >
            Imprimir / PDF
          </Button>
        </DialogActions>
      </Dialog>

      {/* Menú de acciones agrupadas */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        PaperProps={{ sx: { borderRadius: 2.5, minWidth: 280, boxShadow: FP.shadow.md } }}
      >
        {ACCIONES.filter((g) => g.items.some((i) => i.visible)).map((g) => [
          <ListSubheader
            key={`h-${g.grupo}`}
            disableSticky
            sx={{
              lineHeight: 2.2,
              fontSize: "0.68rem",
              fontWeight: 800,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              color: FP.color.textMuted,
              bgcolor: "transparent",
            }}
          >
            {g.grupo}
          </ListSubheader>,
          ...g.items
            .filter((i) => i.visible)
            .map((i) => {
              const Icono = i.icono;
              return (
                <MenuItem
                  key={i.clave}
                  onClick={() => {
                    setMenuAnchor(null);
                    setAccion(i.clave);
                  }}
                  sx={{ py: 1, fontSize: "0.88rem", fontWeight: 600 }}
                >
                  <ListItemIcon>
                    <Icono sx={{ fontSize: 20, color: FP.color.navy }} />
                  </ListItemIcon>
                  <ListItemText primaryTypographyProps={{ fontSize: "0.88rem", fontWeight: 600 }}>
                    {i.etiqueta}
                  </ListItemText>
                </MenuItem>
              );
            }),
        ])}
      </Menu>

      {/* Acciones: cada pantalla existente a pantalla completa, precargada
          con este préstamo cuando la pantalla lo soporta (noprestInicial). */}
      {/* Solicitar incauto: el formulario trae su propia barra de volver. */}
      {accion === "solicitudincauto" && (
        <Dialog fullScreen open onClose={cerrarAccion}>
          <NuevaSolicitudIncauto
            noprestInicial={r.noprest}
            onExit={cerrarAccion}
            onCreado={cerrarAccion}
          />
        </Dialog>
      )}

      {accion && accion !== "cambiarchasis" && accion !== "mora" && accion !== "solicitudincauto" && (
        <Dialog fullScreen open onClose={cerrarAccion}>
          <BarraVolver onExit={cerrarAccion} />
          {accion === "cobrar" && <CobroPagos noprestInicial={r.noprest} />}
          {accion === "recibos" && <MantenimientoPagos noprestInicial={r.noprest} />}
          {accion === "abono" && <AbonosCapital noprestInicial={r.noprest} />}
          {accion === "descinteres" && <DescuentosInteres noprestInicial={r.noprest} />}
          {accion === "modificar" && <ModificacionPrestamos noprestInicial={r.noprest} />}
          {accion === "refinanciar" && <RefinanciamientoInterno noprestInicial={r.noprest} />}
          {accion === "sucursal" && <CambioSucursalPrestamo noprestInicial={r.noprest} />}
          {accion === "crediticio" && <ReporteCrediticio noprestInicial={r.noprest} />}
          {accion === "matricula" && <CobroMatricula chasisInicial={r.chasis} />}
          {accion === "traspaso" && <TraspasarMatricula chasisInicial={r.chasis} />}
          {accion === "historial" && <HistorialChasis chasisInicial={r.chasis} />}
          {accion === "cargo" && <RegistrarCargo noprestInicial={r.noprest} />}
          {accion === "incauto" && <RegistrarIncauto noprestInicial={r.noprest} />}
          {accion === "incautosvig" && <MantenimientoIncautos />}
          {accion === "modincauto" && <ModificarIncautoCargo />}
        </Dialog>
      )}

      {/* Confirmar cancelación de un pago específico */}
      <Dialog
        open={Boolean(pagoCancelar)}
        onClose={() => setPagoCancelar(null)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, display: "flex", alignItems: "center", gap: 1.2 }}>
          <HighlightOffIcon sx={{ color: FP.color.danger }} />
          Cancelar pago
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "0.9rem", color: FP.color.textSecondary }}>
            ¿Cancelar el recibo <b>{pagoCancelar?.norecibo}</b> del{" "}
            <b>{pagoCancelar?.fecha}</b> por{" "}
            <b>RD$ {fmtMoney(pagoCancelar?.total)}</b>? Las cuotas cubiertas por este
            pago vuelven a quedar pendientes.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setPagoCancelar(null)} sx={btnGhost}>
            Volver
          </Button>
          <Button
            startIcon={cancelandoPago ? <CircularProgress size={16} color="inherit" /> : <HighlightOffIcon />}
            onClick={cancelarPago}
            disabled={cancelandoPago}
            sx={btnSolid(FP.color.danger, "#BE123C")}
          >
            Cancelar pago
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modificar cargo */}
      <Dialog
        open={Boolean(cargoEditar)}
        onClose={() => setCargoEditar(null)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, display: "flex", alignItems: "center", gap: 1.2 }}>
          <EditOutlinedIcon sx={{ color: FP.color.navy }} />
          Modificar cargo
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "0.85rem", color: FP.color.textSecondary, mb: 2 }}>
            Cargo del {cargoEditar?.fecha}. El nuevo valor se sincroniza con el préstamo.
          </Typography>
          <Stack spacing={2}>
            <TextField
              fullWidth
              type="number"
              label="Valor"
              value={cargoValor}
              onChange={(e) => setCargoValor(e.target.value)}
              sx={fpInputSx}
            />
            <TextField
              fullWidth
              label="Razón"
              value={cargoRazon}
              onChange={(e) => setCargoRazon(e.target.value)}
              sx={fpInputSx}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setCargoEditar(null)} sx={btnGhost}>
            Cerrar
          </Button>
          <Button
            startIcon={guardandoCargo ? <CircularProgress size={16} color="inherit" /> : <EditOutlinedIcon />}
            onClick={guardarCargo}
            disabled={guardandoCargo}
            sx={btnSolid(FP.color.navy, FP.color.navySoft)}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Eliminar cargo (cancelación con razón) */}
      <Dialog
        open={Boolean(cargoEliminar)}
        onClose={() => setCargoEliminar(null)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, display: "flex", alignItems: "center", gap: 1.2 }}>
          <HighlightOffIcon sx={{ color: FP.color.danger }} />
          Eliminar cargo
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "0.9rem", color: FP.color.textSecondary, mb: 2 }}>
            ¿Cancelar el cargo del <b>{cargoEliminar?.fecha}</b> por{" "}
            <b>RD$ {fmtMoney(cargoEliminar?.valor)}</b>? Se revierte su efecto sobre el préstamo.
          </Typography>
          <TextField
            fullWidth
            label="Razón de la cancelación"
            value={razonEliminar}
            onChange={(e) => setRazonEliminar(e.target.value)}
            sx={fpInputSx}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setCargoEliminar(null)} sx={btnGhost}>
            Volver
          </Button>
          <Button
            startIcon={eliminandoCargo ? <CircularProgress size={16} color="inherit" /> : <HighlightOffIcon />}
            onClick={eliminarCargo}
            disabled={eliminandoCargo}
            sx={btnSolid(FP.color.danger, "#BE123C")}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modificar incauto (solo valor) */}
      <Dialog
        open={Boolean(incautoEditar)}
        onClose={() => setIncautoEditar(null)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, display: "flex", alignItems: "center", gap: 1.2 }}>
          <EditOutlinedIcon sx={{ color: FP.color.navy }} />
          Modificar incauto
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "0.85rem", color: FP.color.textSecondary, mb: 2 }}>
            Incauto del {incautoEditar?.fecha}
            {incautoEditar?.chasis ? ` (chasis ${incautoEditar.chasis})` : ""}.
          </Typography>
          <TextField
            fullWidth
            type="number"
            label="Valor"
            value={incautoValor}
            onChange={(e) => setIncautoValor(e.target.value)}
            sx={fpInputSx}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setIncautoEditar(null)} sx={btnGhost}>
            Cerrar
          </Button>
          <Button
            startIcon={guardandoIncauto ? <CircularProgress size={16} color="inherit" /> : <EditOutlinedIcon />}
            onClick={guardarIncauto}
            disabled={guardandoIncauto}
            sx={btnSolid(FP.color.navy, FP.color.navySoft)}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Eliminar incauto (cancelación con razón) */}
      <Dialog
        open={Boolean(incautoEliminar)}
        onClose={() => setIncautoEliminar(null)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, display: "flex", alignItems: "center", gap: 1.2 }}>
          <HighlightOffIcon sx={{ color: FP.color.danger }} />
          Eliminar incauto
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "0.9rem", color: FP.color.textSecondary, mb: 2 }}>
            ¿Cancelar el incauto del <b>{incautoEliminar?.fecha}</b> por{" "}
            <b>RD$ {fmtMoney(incautoEliminar?.valor)}</b>? El vehículo vuelve a estado disponible.
          </Typography>
          <TextField
            fullWidth
            label="Razón de la cancelación"
            value={razonEliminarIncauto}
            onChange={(e) => setRazonEliminarIncauto(e.target.value)}
            sx={fpInputSx}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setIncautoEliminar(null)} sx={btnGhost}>
            Volver
          </Button>
          <Button
            startIcon={eliminandoIncauto ? <CircularProgress size={16} color="inherit" /> : <HighlightOffIcon />}
            onClick={eliminarIncauto}
            disabled={eliminandoIncauto}
            sx={btnSolid(FP.color.danger, "#BE123C")}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cambiar chasis del préstamo (mini-diálogo propio) */}
      <Dialog
        open={accion === "cambiarchasis"}
        onClose={cerrarAccion}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Cambiar chasis</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "0.9rem", color: FP.color.textSecondary, mb: 2 }}>
            El chasis actual ({r.chasis || "—"}) vuelve al inventario y el nuevo queda
            asignado a la factura y al préstamo. El chasis nuevo debe estar disponible.
          </Typography>
          <TextField
            fullWidth
            label="Chasis nuevo"
            value={chasisNuevo}
            onChange={(e) => setChasisNuevo(e.target.value)}
            sx={fpInputSx}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={cerrarAccion} sx={btnGhost}>
            Cerrar
          </Button>
          <Button
            startIcon={cambiandoChasis ? <CircularProgress size={16} color="inherit" /> : undefined}
            onClick={cambiarChasis}
            disabled={cambiandoChasis}
            sx={btnSolid(FP.color.navy, FP.color.navySoft)}
          >
            Cambiar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Mora y días de gracia del préstamo (mini-diálogo propio) */}
      <Dialog
        open={accion === "mora"}
        onClose={cerrarAccion}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Mora y días de gracia</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "0.9rem", color: FP.color.textSecondary, mb: 2 }}>
            Aplica solo a este préstamo y la mora pendiente se recalcula con los valores nuevos.
            {r.tasamora == null || r.diasgracia == null
              ? ` Hoy usa la configuración general (${r.moraconfig}% y ${r.graciaconfig} días).`
              : ` Configuración general: ${r.moraconfig}% y ${r.graciaconfig} días.`}
          </Typography>
          <Stack spacing={2}>
            <TextField
              fullWidth
              label="Mora % mensual"
              type="number"
              inputProps={{ min: 0, max: 100, step: "0.01" }}
              value={moraNueva}
              onChange={(e) => setMoraNueva(e.target.value)}
              sx={fpInputSx}
            />
            <TextField
              fullWidth
              label="Días de gracia"
              type="number"
              inputProps={{ min: 0, max: 365, step: 1 }}
              value={graciaNueva}
              onChange={(e) => setGraciaNueva(e.target.value)}
              sx={fpInputSx}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={cerrarAccion} sx={btnGhost}>
            Cerrar
          </Button>
          <Button
            startIcon={cambiandoMora ? <CircularProgress size={16} color="inherit" /> : undefined}
            onClick={cambiarMora}
            disabled={cambiandoMora}
            sx={btnSolid(FP.color.navy, FP.color.navySoft)}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Asignar / cambiar gestor de cobros */}
      <Dialog
        open={asignarOpen}
        onClose={() => setAsignarOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, display: "flex", alignItems: "center", gap: 1.2 }}>
          <SupportAgentOutlinedIcon sx={{ color: FP.color.navy }} />
          Asignar gestor de cobros
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "0.85rem", color: FP.color.textSecondary, mb: 2 }}>
            {gestion?.gestor?.gestor
              ? `Gestor actual: ${gestion.gestor.gestor} (desde ${gestion.gestor.fechaasignacion}).`
              : "Este préstamo no tiene gestor asignado."}
          </Typography>
          <Stack spacing={2}>
            <TextField
              select
              fullWidth
              label="Gestor"
              value={gestorSel}
              onChange={(e) => setGestorSel(e.target.value)}
              sx={fpInputSx}
            >
              <MenuItem value="">— Sin gestor —</MenuItem>
              {(gestion?.usuarios || []).map((u) => (
                <MenuItem key={u.usuario} value={u.usuario}>
                  {u.usuario}
                  {u.sucursal ? ` · ${u.sucursal}` : ""}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              label="Nota (opcional)"
              value={gestorNota}
              onChange={(e) => setGestorNota(e.target.value)}
              inputProps={{ maxLength: 300 }}
              sx={fpInputSx}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setAsignarOpen(false)} sx={btnGhost}>
            Cerrar
          </Button>
          <Button
            startIcon={asignando ? <CircularProgress size={16} color="inherit" /> : <SupportAgentOutlinedIcon />}
            onClick={asignarGestor}
            disabled={asignando}
            sx={btnSolid(FP.color.navy, FP.color.navySoft)}
          >
            {gestorSel ? "Asignar" : "Retirar gestor"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Registrar gestión de cobro (nota inmutable) */}
      <Dialog
        open={notaOpen}
        onClose={() => setNotaOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, display: "flex", alignItems: "center", gap: 1.2 }}>
          <ForumOutlinedIcon sx={{ color: FP.color.navy }} />
          Registrar gestión
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "0.85rem", color: FP.color.textSecondary, mb: 2 }}>
            La gestión queda en el historial del préstamo y no se puede editar ni borrar.
          </Typography>
          <Stack spacing={2}>
            <TextField
              select
              fullWidth
              label="Tipo de gestión"
              value={notaTipo}
              onChange={(e) => setNotaTipo(e.target.value)}
              sx={fpInputSx}
            >
              {TIPOS_GESTION.map((t) => (
                <MenuItem key={t.valor} value={t.valor}>
                  {t.label}
                </MenuItem>
              ))}
            </TextField>
            {notaTipo === "promesa" && (
              <Stack direction="row" spacing={2}>
                <TextField
                  fullWidth
                  type="date"
                  label="Fecha prometida"
                  value={notaFechaPromesa}
                  onChange={(e) => setNotaFechaPromesa(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
                <TextField
                  fullWidth
                  type="number"
                  label="Monto prometido"
                  value={notaMontoPromesa}
                  onChange={(e) => setNotaMontoPromesa(e.target.value)}
                  sx={fpInputSx}
                />
              </Stack>
            )}
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Nota"
              value={notaTexto}
              onChange={(e) => setNotaTexto(e.target.value)}
              inputProps={{ maxLength: 1000 }}
              sx={fpMultilineInputSx}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setNotaOpen(false)} sx={btnGhost}>
            Cerrar
          </Button>
          <Button
            startIcon={guardandoNota ? <CircularProgress size={16} color="inherit" /> : <ForumOutlinedIcon />}
            onClick={registrarNota}
            disabled={guardandoNota}
            sx={btnSolid(FP.color.navy, FP.color.navySoft)}
          >
            Registrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Detalle de la venta: factmot completo + campos personalizados,
          con edición rápida de los datos generales del cliente */}
      <EditarGpsDialog
        venta={editarGps}
        onClose={() => setEditarGps(null)}
        onSaved={() => setVersion((v) => v + 1)}
      />
      <DetalleVentaDialog
        open={ventaOpen}
        onClose={() => setVentaOpen(false)}
        venta={d.venta}
        secciones={seccionesExtra}
        valores={d.datosextraventa}
        valoresCodeudor={d.datosextracodeudor}
        resumen={r}
        puedeEditar={puedeEditarExpediente}
        onSaved={() => setVersion((v) => v + 1)}
      />
    </Box>
  );
}
